import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

const FEE_BUCKET_CHIP = {
  Govt:    "bg-emerald-100 text-emerald-700",
  Regular: "bg-violet-100 text-violet-700",
  Other:   "bg-amber-100 text-amber-800",
  NRI:     "bg-rose-100 text-rose-700",
};

// Highlight-bullet source chips. Rendered next to each bullet on the College Detail page
// so users can tell hard facts (NMC / scraped) apart from LLM recall.
const HL_SOURCE_CHIPS = {
  nmc:           { label: "NMC",      bg: "bg-emerald-100", fg: "text-emerald-700", title: "Sourced from NMC seat matrix" },
  official_site: { label: "Official", bg: "bg-sky-100",     fg: "text-sky-700",     title: "Grounded in the college's official website" },
  llm:           { icon: Sparkle, label: "AI", bg: "bg-red-100", fg: "text-red-600", title: "AI-generated (may be less authoritative)" },
  editor:        { label: "Editor",   bg: "bg-rose-100",    fg: "text-rose-700",    title: "Manually corrected by an admin" },
};

// Fee-origin badge for the Fees Structure section. Authority: PDF (ingested) > Manual > Official (scraped) > AI.
const FEE_SOURCE_META = {
  pdf:      { label: "PDF",      cls: "bg-emerald-100 text-emerald-700", title: "From official PDF/CSV ingestion — most authoritative" },
  manual:   { label: "Manual",   cls: "bg-[#6043EF]/10 text-[#6043EF]", title: "Manually entered by an admin" },
  official: { label: "Official", cls: "bg-sky-100 text-sky-700",         title: "Scraped from the college's official website" },
  ai:       { label: "AI",       cls: "bg-amber-100 text-amber-700",     title: "AI-suggested (least authoritative)" },
};
function resolveFeeSource(bucket, field) {
  if (!bucket) return null;
  const checks = field === "regular_fees" ? ["regular_fees", "private_fees"]
    : field === "private_fees" ? ["private_fees", "regular_fees"] : [field];
  const man = new Set(bucket.manual_fields || []);
  const ai = new Set(bucket.ai_fields || []);
  if (checks.some((f) => man.has(f))) return "manual";
  if (checks.some((f) => ai.has(f))) return "ai";
  const s = bucket.source;
  if (s === "official_scrape") return "official";
  if (s === "manual") return "manual";
  if (s === "ai_llm") return "ai";
  return "pdf";
}
function FeeSourceBadge({ source }) {
  const m = FEE_SOURCE_META[source];
  if (!m) return null;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider align-middle ${m.cls}`}
      title={m.title} data-testid={`fee-source-${source}`}>
      {m.label}
    </span>
  );
}


// `field_sources.<field>` value → provenance chip. Extends the highlight-source
// vocabulary with the two lock-tags used on structured fields.
// - "official" → scraped from the college's official website
// - "nmc_new_establishment" → set by the NMC seat-matrix ingest (authoritative)
// - "editor" → manually corrected by an admin via the Edit College panel/agent tool
// - anything else / missing → treat as AI (LLM enrich).
const FIELD_SOURCE_TO_CHIP = {
  official:               HL_SOURCE_CHIPS.official_site,
  nmc_new_establishment:  HL_SOURCE_CHIPS.nmc,
  editor:                 HL_SOURCE_CHIPS.editor,
};
function chipForField(fieldSources, fieldName) {
  const raw = fieldSources && fieldSources[fieldName];
  return FIELD_SOURCE_TO_CHIP[raw] || HL_SOURCE_CHIPS.llm;
}

// Format an INR/USD amount compactly for a table cell.
// 1,53,571 → "₹1.5L", 40,11,950 → "₹40.1L", 1,25,00,000 → "₹1.25Cr"
function formatFeeShort(amount, currency = "INR") {
  if (amount == null) return "—";
  const cur = currency === "USD" ? "$" : "₹";
  const n = Math.abs(amount);
  if (n >= 1_00_00_000) return `${cur}${(amount / 1_00_00_000).toFixed(amount % 1_00_00_000 ? 2 : 0)}Cr`;
  if (n >= 1_00_000)    return `${cur}${(amount / 1_00_000).toFixed(amount % 1_00_000 ? 1 : 0)}L`;
  if (n >= 1_000)       return `${cur}${(amount / 1_000).toFixed(0)}K`;
  return `${cur}${amount}`;
}

// Render an enrich-diff cell value for both legacy string arrays and the new
// tagged highlights shape (list of {text, source}). Never leaks "[object Object]".
function fmtDiffVal(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    const parts = v.map((x) => (
      typeof x === "string" ? x
      : (x && typeof x === "object" ? (x.text || JSON.stringify(x)) : String(x))
    ));
    return parts.join(" • ");
  }
  return String(v);
}
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Buildings, MapPin, CurrencyInr, CalendarBlank, Globe, Tag, GraduationCap, Trophy, SealCheck, Certificate, ClockCounterClockwise, ArrowSquareOut, ImageSquare, CheckCircle, CaretLeft, CaretRight, X, Star, Phone, ArrowsLeftRight, Sparkle, ArrowsClockwise, PencilSimple, Plus, ArrowsMerge, ArrowsSplit, MagnifyingGlass, Trash, Info } from "@phosphor-icons/react";
import SplitMasterDialog from "../components/SplitMasterDialog";
import { getCollege, formatFees, formatRound, CATEGORY_COLORS, ASSET_BASE, setCollegeBanner, deleteCollegeImage, getSiteConfig, getFilterOptions, enrichCollegePreview, enrichCollegeApply, forceScrapeCollege, editCollegeFields, syncCollegeLocation, verifyDeemedPreview, verifyDeemedApply, removeCollegeAlias, clearNmcName, addMasterAlias, setMasterDisplayName, addMasterCode, getCourseCatalog, adminCollegesLiteSearch, mergeMasterPreview, mergeMasterApply, mergeMasterUndo, setCollegeFee, moveCollegeFee, removeCollegeFeeYear, getInternalCodes, setCanonicalCode, getCollegeSeatMatrixRows, deleteCollegeSeatMatrixRows, deleteCollegeSeatMatrixCategories } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useInrToggle, initShowInrDefault } from "../lib/inrToggle";
import { CollegeNews } from "../components/CollegeNews";
import { EligibilityPredictor, LeadCallbackCard } from "../components/EligibilityPredictor";
import { useAuth } from "../lib/AuthProvider";
import { toast } from "sonner";

// Admin-editable fee block for ONE (year · course). Reuses the surgical backend
// tools (set / move / remove) via REST. Value cells, plus header year/course edit + delete.
const FEE_EDIT_KIND = { govt_fees: "govt", private_fees: "private", other_fees: "management", nri_fees: "nri" };
// Chip styling for a college's coordinate provenance (geo_source).
const GEO_SOURCE_STYLE = {
  google_places: "bg-blue-100 text-blue-700",
  editor: "bg-[#6043EF]/10 text-[#6043EF]",
  gemini: "bg-violet-100 text-violet-700",
  "photon-college": "bg-amber-100 text-amber-700",
  photon: "bg-amber-100 text-amber-700",
};

function FeeCourseBlock({ yearKey, course, data, code, isAdmin, showInr, setShowInr, usdRate, onChanged }) {
  const [editField, setEditField] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [hdrOpen, setHdrOpen] = useState(false);
  const [newCourse, setNewCourse] = useState(course);
  const [newYear, setNewYear] = useState(yearKey);
  const [hdrBusy, setHdrBusy] = useState(false);

  const fmtFee = (val, cur) => {
    if (!val && val !== 0) return "N/A";
    if (cur === "USD") return `$${val.toLocaleString()}`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${val.toLocaleString()}`;
  };

  const startEdit = (key) => { setEditField(key); setEditVal(String(data[key] ?? "")); };
  const saveValue = async (key) => {
    const kind = FEE_EDIT_KIND[key];
    if (!kind) return;
    setSavingField(true);
    try {
      await setCollegeFee({ college_code: code, fee_kind: kind, value: editVal, year: yearKey, course });
      toast.success(`${key.replace(/_/g, " ")} updated`);
      setEditField(null);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    setSavingField(false);
  };

  const saveHeader = async () => {
    const patch = { college_code: code, year: yearKey, course };
    if (newCourse && newCourse.toUpperCase() !== course.toUpperCase()) patch.to_course = newCourse.toUpperCase();
    if (newYear && newYear !== yearKey) patch.to_year = newYear;
    if (!patch.to_course && !patch.to_year) { setHdrOpen(false); return; }
    setHdrBusy(true);
    try {
      await moveCollegeFee(patch);
      toast.success("Fee structure moved");
      setHdrOpen(false);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
    setHdrBusy(false);
  };

  const deleteCourse = async () => {
    if (!window.confirm(`Delete ${course} fee structure for AY ${yearKey}? This cannot be undone.`)) return;
    setHdrBusy(true);
    try {
      await removeCollegeFeeYear({ college_code: code, year: yearKey, course });
      toast.success("Fee structure deleted");
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setHdrBusy(false);
  };

  const skipRegular = data.private_fees != null && data.regular_fees != null && data.private_fees === data.regular_fees;
  const fields = [
    { key: "govt_fees", label: "GOVT FEES", currency_key: "govt_currency", primary: true },
    { key: "private_fees", label: "PRIVATE FEES", currency_key: "private_currency" },
    ...(!skipRegular ? [{ key: "regular_fees", label: "REGULAR FEES", currency_key: "regular_currency" }] : []),
    { key: "other_fees", label: "OTHER FEES", currency_key: null },
    { key: "nri_fees", label: "NRI FEES", currency_key: "nri_currency" },
  ].filter((f) => data[f.key] != null && data[f.key] !== undefined);

  return (
    <div>
      <div className="px-3 py-1.5 bg-[#F4F4F5] border-b border-zinc-200 flex items-center gap-2" data-testid={`fee-course-header-${yearKey}-${course}`}>
        <span className="font-['IBM_Plex_Mono'] text-[10px] font-bold text-[#09090B]">{course}</span>
        <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#52525B]">AY {yearKey}</span>
        {data.course_label && <Badge variant="outline" className="text-[8px]">{data.course_label}</Badge>}
        {data.full_course_fees && <Badge className="bg-[#FFC107]/20 text-[#92700C] text-[8px]">FULL COURSE FEES</Badge>}
        {isAdmin && (
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setHdrOpen((o) => !o)} title="Edit year / course" data-testid={`fee-edit-header-${yearKey}-${course}`}
              className="h-6 w-6 grid place-items-center rounded hover:bg-zinc-200 text-[#52525B] hover:text-[#6043EF]"><PencilSimple size={12} weight="bold" /></button>
            <button onClick={deleteCourse} disabled={hdrBusy} title="Delete this fee structure" data-testid={`fee-delete-${yearKey}-${course}`}
              className="h-6 w-6 grid place-items-center rounded hover:bg-rose-100 text-[#52525B] hover:text-rose-600"><Trash size={12} weight="bold" /></button>
          </div>
        )}
      </div>
      {isAdmin && hdrOpen && (
        <div className="px-3 py-2 bg-[#6043EF]/5 border-b border-zinc-200 flex flex-wrap items-end gap-3" data-testid={`fee-header-editor-${yearKey}-${course}`}>
          <label className="flex flex-col gap-0.5">
            <span className="text-[8px] font-black uppercase tracking-wider text-zinc-500">Course</span>
            <input value={newCourse} onChange={(e) => setNewCourse(e.target.value)} data-testid="fee-edit-course-input"
              className="w-24 h-7 px-2 rounded border border-zinc-300 text-[11px] font-mono uppercase focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[8px] font-black uppercase tracking-wider text-zinc-500">Academic Year</span>
            <input value={newYear} onChange={(e) => setNewYear(e.target.value)} placeholder="2026-27" data-testid="fee-edit-year-input"
              className="w-24 h-7 px-2 rounded border border-zinc-300 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
          </label>
          <button onClick={saveHeader} disabled={hdrBusy} data-testid="fee-header-save"
            className="h-7 px-3 rounded bg-[#6043EF] text-white text-[10px] font-black uppercase tracking-wider hover:bg-[#4c34c9] disabled:opacity-50">{hdrBusy ? "…" : "Save"}</button>
          <button onClick={() => { setHdrOpen(false); setNewCourse(course); setNewYear(yearKey); }} className="h-7 px-2 rounded text-[10px] font-bold text-zinc-500 hover:bg-zinc-100">Cancel</button>
          <span className="text-[9px] text-zinc-400">Won't overwrite an existing target.</span>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-0">
        {fields.map((f) => {
          const cur = f.currency_key ? data[f.currency_key] : null;
          const isUsd = cur === "USD";
          const inrMode = isUsd && showInr;
          const shownVal = inrMode ? fmtFee(Math.round(data[f.key] * usdRate), "INR") : fmtFee(data[f.key], cur);
          const editable = isAdmin && FEE_EDIT_KIND[f.key];
          return (
            <div key={f.key} className="p-4 border-r border-b border-zinc-200 group relative">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] mb-1 flex items-center gap-1.5">
                {f.label}
                <FeeSourceBadge source={resolveFeeSource(data, f.key)} />
                {editable && editField !== f.key && (
                  <button onClick={() => startEdit(f.key)} title={`Edit ${f.label}`} data-testid={`fee-edit-${f.key}-${yearKey}-${course}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-4 w-4 grid place-items-center rounded text-zinc-400 hover:text-[#6043EF]"><PencilSimple size={11} weight="bold" /></button>
                )}
              </span>
              {editField === f.key ? (
                <div className="flex items-center gap-1 mt-1">
                  <input autoFocus value={editVal} onChange={(e) => setEditVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveValue(f.key); if (e.key === "Escape") setEditField(null); }}
                    data-testid={`fee-edit-input-${f.key}`}
                    className="w-24 h-7 px-2 rounded border border-[#6043EF] text-[13px] font-mono focus:outline-none" />
                  <button onClick={() => saveValue(f.key)} disabled={savingField} className="h-7 w-7 grid place-items-center rounded bg-[#6043EF] text-white" data-testid={`fee-save-${f.key}`}><CheckCircle size={13} weight="bold" /></button>
                  <button onClick={() => setEditField(null)} className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100"><X size={13} weight="bold" /></button>
                </div>
              ) : (
                <span className={`font-['IBM_Plex_Mono'] text-xl font-bold ${f.primary ? "text-[#09090B]" : "text-[#52525B]"}`}>{shownVal}</span>
              )}
              {isUsd && editField !== f.key && (
                <button type="button" onClick={() => setShowInr(!showInr)} title="Toggle USD / INR (applies site-wide)"
                  className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-bold leading-none px-1.5 py-1 border border-zinc-300 text-zinc-600 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors rounded align-middle" data-testid="detail-fee-currency-toggle">
                  <ArrowsLeftRight size={9} weight="bold" />{inrMode ? "$" : "₹"}
                </button>
              )}
            </div>
          );
        })}
        {!data.govt_fees && !data.private_fees && !data.regular_fees && !data.other_fees && !data.nri_fees && (
          <div className="p-4 border-b border-zinc-200 col-span-full"><span className="text-sm text-zinc-400">No fee data available</span></div>
        )}
      </div>
      {(data.donation || data.extra) && (
        <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-3 flex-wrap text-[10px] text-[#52525B]">
          {(data.extra?.hostel_fees || data.donation?.hostel) && (<span>Hostel: <strong className="font-['IBM_Plex_Mono']">{data.extra?.hostel_fees ? fmtFee(data.extra.hostel_fees) : data.donation.hostel}</strong></span>)}
          {(data.extra?.security_deposit || data.donation?.security_deposit) && (<span>Security Deposit: <strong className="font-['IBM_Plex_Mono']">{data.extra?.security_deposit ? fmtFee(data.extra.security_deposit) : data.donation.security_deposit}</strong></span>)}
          {data.extra?.other_charges && (<span>Other Charges: <strong className="font-['IBM_Plex_Mono']">{fmtFee(data.extra.other_charges)}</strong></span>)}
          {data.donation?.misc && (<span>Misc: <strong className="font-['IBM_Plex_Mono']">{data.donation.misc}</strong></span>)}
        </div>
      )}
    </div>
  );
}


export default function CollegeDetail() {
  const { code } = useParams();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAliases, setShowAliases] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [settingBanner, setSettingBanner] = useState(null);
  const [cutoffFilters, setCutoffFilters] = useState({ year: "all", round: "all", body: "all", quota: "all", category: [], course: "all" });
  const [showInr, setShowInr] = useInrToggle();
  const [usdRate, setUsdRate] = useState(83);
  const [reEnrich, setReEnrich] = useState({ open: false, loading: false, applying: false, changes: null, name: "", selected: {} });
  const [vd, setVd] = useState({ open: false, loading: false, applying: false, data: null });
  const [editPanel, setEditPanel] = useState({ open: false, saving: false, form: {} });
  const [locSync, setLocSync] = useState({ busy: false, done: false }); // AI location preview in edit panel
  const [locSource, setLocSource] = useState(""); // provenance of the coords in the edit panel (google_places / editor / …)
  const [dnEdit, setDnEdit] = useState({ open: false, value: "", saving: false }); // inline display-name editor
  const [courseOptions, setCourseOptions] = useState([]); // curated courses from predictor cache
  const [stateOptions, setStateOptions] = useState([]); // canonical states from the colleges filter
  const [showAddCourse, setShowAddCourse] = useState(false);

  useEffect(() => {
    loadCollege();
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getSiteConfig().then((r) => {
      setUsdRate(r.data.usd_inr_rate || 83);
      initShowInrDefault(!!r.data.show_fees_in_inr);
    }).catch(() => {});
  }, []);

  const imagesCount = data?.college?.images_count || 0;

  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () => setLightboxIndex((i) => (i + 1) % imagesCount);
  const prevImage = () => setLightboxIndex((i) => (i - 1 + imagesCount) % imagesCount);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") nextImage();
      else if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCollege = async () => {
    setLoading(true);
    try {
      const res = await getCollege(code);
      setData(res.data);
    } catch (e) {
      console.error("Failed to load college", e);
    }
    setLoading(false);
  };

  const openReEnrich = async (forceScrape = false) => {
    setReEnrich({ open: true, loading: true, applying: false, changes: null, name: "", selected: {}, forceScrape });
    try {
      const res = await enrichCollegePreview(code, { forceScrape });
      if (res.data.error) {
        toast.error(res.data.error);
        setReEnrich((p) => ({ ...p, open: false, loading: false }));
        return;
      }
      const changes = res.data.changes || {};
      const selected = Object.fromEntries(Object.keys(changes).map((f) => [f, true]));
      setReEnrich({ open: true, loading: false, applying: false, changes, name: res.data.name || "", selected, forceScrape });
    } catch (e) {
      toast.error("Re-enrich preview failed");
      setReEnrich((p) => ({ ...p, open: false, loading: false }));
    }
  };

  const forceScrape = async () => {
    if (!window.confirm("Force re-scrape of the official website? This ignores the 6-month freshness cache.")) return;
    try {
      const res = await forceScrapeCollege(code);
      const s = res.data.status;
      if (s === "done") toast.success(`Scraped: ${(res.data.fields || []).join(", ") || "no new facts"}`);
      else if (s === "no_website") toast.error("This college has no official website configured.");
      else if (s === "unreachable") toast.error("Website unreachable — try again later.");
      else toast.error(`Scrape failed: ${s}`);
      await loadCollege();
    } catch (e) {
      toast.error("Force scrape failed");
    }
  };

  const openEditPanel = () => {
    const c = data?.college || {};
    setShowAddCourse(false);
    if (!courseOptions.length) {
      getCourseCatalog().then((r) => setCourseOptions(r.data?.courses || [])).catch(() => {});
    }
    if (!stateOptions.length) {
      getFilterOptions().then((r) => setStateOptions(r.data?.states || [])).catch(() => {});
    }
    setEditPanel({
      open: true,
      saving: false,
      form: {
        official_website: c.official_website || "",
        established_year: c.established_year || "",
        college_type: c.college_type || "",
        college_status: c.college_status || "",
        affiliate_university: c.affiliate_university || "",
        phone: c.phone || "",
        location: c.location || c.city || "",
        city: c.city || c.location || "",
        state: c.state || "",
        lat: c.lat != null ? String(c.lat) : "",
        lng: c.lng != null ? String(c.lng) : "",
        courses: Array.isArray(c.courses) ? [...c.courses] : [],
      },
    });
    setLocSync({ busy: false, done: false });
    setLocSource(c.geo_source || "");
  };
  // City and the (city||location) header field are kept in lockstep so a single input fixes both.
  const setCityField = (v) => setEditPanel((p) => ({ ...p, form: { ...p.form, location: v, city: v } }));
  // Any hand-edit of a coordinate re-attributes it to the editor.
  const setCoordField = (field, v) => { setEditField(field, v); setLocSource("editor"); };
  // Match a typed state to a canonical one, tolerating case and the &/"and" variant.
  const _normLoose = (x) => (x || "").trim().toLowerCase().replace(/\s*&\s*/g, " and ").replace(/\s+/g, " ").trim();
  const matchState = (v) => stateOptions.find((s) => _normLoose(s) === _normLoose(v)) || null;

  const setEditField = (field, value) => setEditPanel((p) => ({ ...p, form: { ...p.form, [field]: value } }));

  const addCourseToForm = (course) => setEditPanel((p) =>
    (p.form.courses || []).includes(course) ? p
    : { ...p, form: { ...p.form, courses: [...(p.form.courses || []), course] } });

  const removeCourseFromForm = (idx) => setEditPanel((p) => ({
    ...p,
    form: { ...p.form, courses: (p.form.courses || []).filter((_, i) => i !== idx) },
  }));

  const [removingAlias, setRemovingAlias] = useState(null); // index currently removing
  const removeAlias = async (a, i) => {
    setRemovingAlias(i);
    try {
      await removeCollegeAlias(code, a.name, a.source);
      await loadCollege();
      toast.success(`Removed alias "${a.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to remove alias");
    } finally {
      setRemovingAlias(null);
    }
  };

  const [clearingNmcName, setClearingNmcName] = useState(false);
  const removeNmcName = async () => {
    setClearingNmcName(true);
    try {
      await clearNmcName(code);
      await loadCollege();
      toast.success("Cleared NMC name");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to clear NMC name");
    } finally {
      setClearingNmcName(false);
    }
  };

  // Set the college's display_name to one of its known names (clickable name-labels).
  const [settingDisplay, setSettingDisplay] = useState(false);
  const applyDisplayName = async (name, label) => {
    const val = (name || "").trim();
    if (!val) return;
    if (!window.confirm(`Set the DISPLAY NAME for this college to the ${label} name?\n\n"${val}"\n\nThis is what shows across the site.`)) return;
    setSettingDisplay(true);
    try {
      await setMasterDisplayName(code, val);
      await loadCollege();
      toast.success(`Display name set to "${val}"`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to set display name");
    } finally {
      setSettingDisplay(false);
    }
  };

  // Manual add of a NAME alias / a CODE into the key_registry (admin_manual).
  const [addAlias, setAddAlias] = useState({ open: false, value: "", saving: false });
  const [splitOpen, setSplitOpen] = useState(false);
  const submitAddAlias = async () => {
    const name = addAlias.value.trim();
    if (!name) return;
    setAddAlias((s) => ({ ...s, saving: true }));
    try {
      const { data } = await addMasterAlias(code, name);
      if (data?.added) {
        await loadCollege();
        toast.success(data.already ? "Alias already present" : `Added name "${name}"`);
        setAddAlias({ open: false, value: "", saving: false });
      } else {
        toast.error(data?.message || "Could not add name");
        setAddAlias((s) => ({ ...s, saving: false }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to add name");
      setAddAlias((s) => ({ ...s, saving: false }));
    }
  };

  const [addCode, setAddCode] = useState({ open: false, body: "", value: "", saving: false });
  // ── Internal (COLLEGE_CODE-namespace) codes + set-canonical ──
  const [internalCodes, setInternalCodes] = useState(null);
  const [switchingCode, setSwitchingCode] = useState(null);
  const loadInternalCodes = async () => {
    if (!isAdmin || !code) return;
    try { const { data } = await getInternalCodes(code); setInternalCodes(data); }
    catch { setInternalCodes(null); }
  };
  useEffect(() => { loadInternalCodes(); /* eslint-disable-next-line */ }, [code, isAdmin]);
  const makePrimaryCode = async (newCode, counts) => {
    const cur = internalCodes?.canonical || code;
    if (newCode === cur) return;
    const rows = counts ? counts.total : "";
    if (!window.confirm(`Change primary code\n\n${cur} → ${newCode}\n\nThis re-points ${rows} row(s) across allotments, cutoffs, results, seat-matrix & MCC onto "${newCode}", keeps "${cur}" resolvable, and reindexes search. Continue?`)) return;
    setSwitchingCode(newCode);
    try {
      const { data } = await setCanonicalCode(cur, newCode);
      toast.success(`Primary code is now ${data.new_code}`);
      window.location.href = `/colleges/${encodeURIComponent(data.new_code)}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change primary code");
      setSwitchingCode(null);
    }
  };
  const submitAddCode = async () => {
    const body = addCode.body.trim();
    const val = addCode.value.trim();
    if (!body || !val) return;
    setAddCode((s) => ({ ...s, saving: true }));
    try {
      const { data } = await addMasterCode(code, body, val);
      if (data?.added) {
        await loadCollege();
        toast.success(data.already ? "Code already present" : `Added ${body} code "${val}"`);
        setAddCode({ open: false, body: "", value: "", saving: false });
      } else {
        toast.error(data?.message || "Could not add code");
        setAddCode((s) => ({ ...s, saving: false }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to add code");
      setAddCode((s) => ({ ...s, saving: false }));
    }
  };

  // ── Merge master (absorb a same-state source college into THIS one) ──
  const [merge, setMerge] = useState({ open: false, q: "", results: [], searching: false, selected: null, preview: null, loading: false, lastMergeId: null, undoing: false });
  const runMergeSearch = async (q) => {
    setMerge((s) => ({ ...s, q, selected: null, preview: null }));
    if (!q || q.trim().length < 2) { setMerge((s) => ({ ...s, results: [] })); return; }
    setMerge((s) => ({ ...s, searching: true }));
    try {
      const { data } = await adminCollegesLiteSearch(q.trim(), college.state || "");
      const rows = (data?.results || data?.colleges || data || []).filter((r) => (r.college_code || r.code) !== code);
      setMerge((s) => ({ ...s, results: rows, searching: false }));
    } catch {
      setMerge((s) => ({ ...s, results: [], searching: false }));
    }
  };
  const selectMergeSource = async (row) => {
    const srcCode = row.college_code || row.code;
    setMerge((s) => ({ ...s, selected: row, loading: true, preview: null }));
    try {
      const { data } = await mergeMasterPreview(code, srcCode);
      setMerge((s) => ({ ...s, preview: data, loading: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview merge");
      setMerge((s) => ({ ...s, loading: false, selected: null }));
    }
  };
  const applyMerge = async () => {
    const srcCode = merge.selected?.college_code || merge.selected?.code;
    setMerge((s) => ({ ...s, loading: true }));
    try {
      const { data } = await mergeMasterApply(code, srcCode);
      const mv = data?.moved || {};
      toast.success(`Merged. Moved ${mv.raw_allotments || 0} allotments, ${mv.counselling_results || 0} results, ${mv.seat_matrix_detail || 0} seat rows.`, {
        action: { label: "Undo", onClick: () => undoMerge(data.merge_id) },
        duration: 12000,
      });
      setMerge((s) => ({ ...s, open: false, loading: false, lastMergeId: data.merge_id, selected: null, preview: null, q: "", results: [] }));
      await loadCollege();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
      setMerge((s) => ({ ...s, loading: false }));
    }
  };
  const undoMerge = async (mid) => {
    setMerge((s) => ({ ...s, undoing: true }));
    try {
      await mergeMasterUndo(mid);
      toast.success("Merge undone — source college restored.");
      setMerge((s) => ({ ...s, undoing: false, lastMergeId: null }));
      await loadCollege();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
      setMerge((s) => ({ ...s, undoing: false }));
    }
  };


  const saveEditPanel = async () => {
    const c = data?.college || {};
    const changed = {};
    Object.entries(editPanel.form).forEach(([f, v]) => {
      if (f === "courses") return; // array — handled separately below
      const cur = c[f] == null ? "" : String(c[f]);
      if (String(v) !== cur && String(v).trim() !== "") changed[f] = v;
    });
    // courses (array): include only if it actually changed
    const curCourses = Array.isArray(c.courses) ? c.courses : [];
    const newCourses = Array.isArray(editPanel.form.courses) ? editPanel.form.courses : [];
    if (JSON.stringify(newCourses) !== JSON.stringify(curCourses)) {
      changed.courses = newCourses;
    }
    // State must be one of the canonical filter names — never save junk.
    if ("state" in changed) {
      const canon = matchState(changed.state);
      if (stateOptions.length && !canon) {
        toast.error(`"${changed.state}" isn't a valid state — pick one from the list`);
        return;
      }
      if (canon) changed.state = canon; // snap to the exact canonical spelling
    }
    // Coordinates carry a provenance tag: pass it through so AI-sourced coords stay
    // attributed to google_places while a hand-typed value is stamped editor.
    if ("lat" in changed || "lng" in changed) {
      changed.geo_source = locSource || "editor";
    }
    if (!Object.keys(changed).length) {
      toast.info("No changes to save");
      return;
    }
    setEditPanel((p) => ({ ...p, saving: true }));
    try {
      await editCollegeFields(code, changed);
      await loadCollege();
      toast.success(`Updated ${Object.keys(changed).length} field(s) — tagged as Editor`);
      setEditPanel({ open: false, saving: false, form: {} });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
      setEditPanel((p) => ({ ...p, saving: false }));
    }
  };

  // AI location preview: Gemini city normaliser + Google Places coords → pre-fill the
  // edit-panel fields (nothing is written until the admin reviews and hits Save).
  const syncLocation = async () => {
    setLocSync({ busy: true, done: false });
    try {
      const r = await syncCollegeLocation(code);
      const p = r.data?.proposed || {};
      if (!Object.keys(p).length) {
        toast.info("AI couldn't determine a better location");
        setLocSync({ busy: false, done: false });
        return;
      }
      setEditPanel((pan) => ({
        ...pan,
        form: {
          ...pan.form,
          ...(p.location != null ? { location: p.location, city: p.location } : {}),
          ...(p.lat != null ? { lat: String(p.lat) } : {}),
          ...(p.lng != null ? { lng: String(p.lng) } : {}),
        },
      }));
      if (p.lat != null && p.lng != null) setLocSource(r.data?.sources?.lat || "google_places");
      const bits = [p.location && `city “${p.location}”`, (p.lat != null && p.lng != null) && "coordinates"].filter(Boolean);
      toast.success(`AI filled ${bits.join(" + ") || "location"} — review & Save`);
      setLocSync({ busy: false, done: true });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Location sync failed");
      setLocSync({ busy: false, done: false });
    }
  };

  // Inline display-name override (fixes a garbled ingested name without touching aliases).
  const saveDisplayName = async () => {
    const v = (dnEdit.value || "").trim();
    if (!v) { toast.error("Display name can't be empty"); return; }
    setDnEdit((p) => ({ ...p, saving: true }));
    try {
      await setMasterDisplayName(code, v);
      await loadCollege();
      toast.success("Display name updated");
      setDnEdit({ open: false, value: "", saving: false });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Update failed");
      setDnEdit((p) => ({ ...p, saving: false }));
    }
  };

  const applyReEnrich = async () => {
    const changes = reEnrich.changes || {};
    const fields = Object.fromEntries(
      Object.entries(changes).filter(([f]) => reEnrich.selected[f]).map(([f, c]) => [f, c.proposed])
    );
    if (!Object.keys(fields).length) { setReEnrich((p) => ({ ...p, open: false })); return; }
    setReEnrich((p) => ({ ...p, applying: true }));
    try {
      await enrichCollegeApply(code, fields);
      await loadCollege();
      toast.success(`Updated ${Object.keys(fields).length} field(s)`);
      setReEnrich({ open: false, loading: false, applying: false, changes: null, name: "", selected: {} });
    } catch (e) {
      toast.error("Apply failed");
      setReEnrich((p) => ({ ...p, applying: false }));
    }
  };

  const openVerifyDeemed = async () => {
    setVd({ open: true, loading: true, applying: false, data: null });
    try {
      const res = await verifyDeemedPreview(code);
      if (res.data.error) { toast.error(res.data.error); setVd({ open: false, loading: false, applying: false, data: null }); return; }
      setVd({ open: true, loading: false, applying: false, data: res.data });
    } catch (e) {
      toast.error("Deemed verify failed");
      setVd({ open: false, loading: false, applying: false, data: null });
    }
  };

  const applyVerifyDeemed = async () => {
    if (!vd.data || (vd.data.recommended_action === "none" && !vd.data.affiliate_changed)) { setVd((p) => ({ ...p, open: false })); return; }
    setVd((p) => ({ ...p, applying: true }));
    try {
      await verifyDeemedApply(code, vd.data.recommended_action !== "none" ? vd.data.proposed_status : "", vd.data.affiliate_changed ? vd.data.proposed_affiliate : "");
      await loadCollege();
      toast.success("College updated");
      setVd({ open: false, loading: false, applying: false, data: null });
    } catch (e) {
      toast.error("Apply failed");
      setVd((p) => ({ ...p, applying: false }));
    }
  };

  const handleSetBanner = async (idx) => {
    try {
      setSettingBanner(idx);
      await setCollegeBanner(code, idx);
      const res = await getCollege(code);
      setData(res.data);
      toast.success("Hero banner updated");
    } catch (e) {
      toast.error("Failed to update banner");
    } finally {
      setSettingBanner(null);
    }
  };

  const [deletingImage, setDeletingImage] = useState(null);
  const handleDeleteImage = async (idx) => {
    if (!window.confirm("Delete this campus photo? This cannot be undone.")) return;
    try {
      setDeletingImage(idx);
      await deleteCollegeImage(code, idx);
      const res = await getCollege(code);
      setData(res.data);
      toast.success("Photo deleted");
    } catch (e) {
      toast.error("Failed to delete photo");
    } finally {
      setDeletingImage(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12">
          <div className="space-y-4">
            <div className="h-8 w-64 bg-zinc-200 animate-pulse" />
            <div className="h-48 bg-zinc-200 animate-pulse" />
            <div className="h-64 bg-zinc-200 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#52525B]">College not found</p>
      </div>
    );
  }

  const { college, seat_matrix, cutoffs } = data;

  const TYPE_BADGE = {
    govt: "bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B]",
    private: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]",
    trust: "bg-[#92700C]/10 text-[#92700C] border-[#92700C]",
    deemed: "bg-[#6043EF]/10 text-[#6043EF] border-[#6043EF]",
  };

  // Group cutoffs by year and round
  const cutoffsByYear = {};
  (cutoffs || []).forEach((c) => {
    const key = `${c.year}-${formatRound(c.round_number)}`;
    if (!cutoffsByYear[key]) cutoffsByYear[key] = [];
    cutoffsByYear[key].push(c);
  });

  // Historical-cutoff column filters (all columns except closing rank)
  const uniq = (arr) => [...new Set(arr.filter((v) => v !== undefined && v !== null && v !== ""))];
  const cutoffOptions = {
    year: uniq((cutoffs || []).map((c) => c.year)).sort((a, b) => b - a),
    round: uniq((cutoffs || []).map((c) => c.round_number)).sort((a, b) => a - b),
    body: uniq((cutoffs || []).map((c) => c.counselling_body)).sort(),
    quota: uniq((cutoffs || []).map((c) => c.quota)).sort(),
    category: uniq((cutoffs || []).map((c) => c.category)).sort(),
    course: uniq((cutoffs || []).map((c) => c.course)).sort(),
  };
  const filteredCutoffs = (cutoffs || []).filter((c) =>
    (cutoffFilters.year === "all" || String(c.year) === cutoffFilters.year) &&
    (cutoffFilters.round === "all" || String(c.round_number) === cutoffFilters.round) &&
    (cutoffFilters.body === "all" || c.counselling_body === cutoffFilters.body) &&
    (cutoffFilters.quota === "all" || c.quota === cutoffFilters.quota) &&
    (cutoffFilters.category.length === 0 || cutoffFilters.category.includes(c.category)) &&
    (cutoffFilters.course === "all" || c.course === cutoffFilters.course)
  );
  const setCF = (k, v) => setCutoffFilters((p) => ({ ...p, [k]: v }));
  const toggleCategory = (cat) => setCutoffFilters((p) => ({
    ...p, category: p.category.includes(cat) ? p.category.filter((x) => x !== cat) : [...p.category, cat],
  }));
  const resetCF = () => setCutoffFilters({ year: "all", round: "all", body: "all", quota: "all", category: [], course: "all" });
  const cfActive = cutoffFilters.category.length > 0 ||
    ["year", "round", "body", "quota", "course"].some((k) => cutoffFilters[k] !== "all");

  // Shared header content — reused by both the banner-overlay path AND the
  // plain-header fallback (no image). Keeps the two branches DRY.
  const renderHeaderContent = () => (
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3 mb-2">
          {college.has_logo && (
            <img
              src={`${ASSET_BASE}/colleges/${college.college_code}/logo`}
              alt={`${college.short_name || college.name} logo`}
              className="h-12 w-12 object-contain bg-white border border-zinc-200 rounded p-1 shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid="college-logo"
            />
          )}
          {dnEdit.open ? (
            <div className="flex items-center gap-2 flex-1 min-w-0" data-testid="display-name-editor">
              <input
                autoFocus
                value={dnEdit.value}
                onChange={(e) => setDnEdit((p) => ({ ...p, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveDisplayName(); if (e.key === "Escape") setDnEdit({ open: false, value: "", saving: false }); }}
                data-testid="display-name-input"
                className="flex-1 min-w-0 h-10 px-3 text-xl sm:text-2xl font-bold text-[#09090B] border-2 border-[#6043EF] rounded-md focus:outline-none focus:ring-2 focus:ring-[#6043EF]/30"
              />
              <button onClick={saveDisplayName} disabled={dnEdit.saving} data-testid="display-name-save"
                className="h-8 px-3 rounded-md bg-[#6043EF] text-white text-[11px] font-black uppercase tracking-wider hover:bg-[#4e35c9] disabled:opacity-50 shrink-0">
                {dnEdit.saving ? "…" : "Save"}</button>
              <button onClick={() => setDnEdit({ open: false, value: "", saving: false })} data-testid="display-name-cancel"
                className="h-8 px-2 rounded-md text-[11px] font-bold text-zinc-500 hover:bg-zinc-100 shrink-0">Cancel</button>
            </div>
          ) : (
            <>
              {isAdmin && (
                <button
                  onClick={() => setDnEdit({ open: true, value: college.display_name || college.short_name || college.name || "", saving: false })}
                  title="Edit display name"
                  data-testid="edit-display-name-btn"
                  className="shrink-0 mt-1.5 h-7 w-7 grid place-items-center rounded-md bg-[#FAFAFA] text-[#52525B] shadow-sm hover:shadow-md hover:text-[#6043EF] hover:bg-white transition-all"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
              )}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-[#09090B] font-heading break-words min-w-0" data-testid="college-name">
                {college.display_name || college.short_name || college.name}
              </h1>
            </>
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="college-admin-toolbar">
            {(college.aliases?.length > 0 || college.codes || college.nmc_data?.name) && (
              <button
                onClick={() => setShowAliases(!showAliases)}
                className={`h-7 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 rounded-md transition-all ${
                  showAliases ? "bg-[#6043EF] text-white shadow-sm" : "bg-[#FAFAFA] text-[#52525B] shadow-sm hover:shadow-md hover:bg-white"
                }`}
                data-testid="show-aliases-btn"
              >
                <Tag size={10} weight="bold" /> ALIASES
              </button>
            )}
            <button
              onClick={() => openReEnrich(false)}
              className="h-7 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 rounded-md bg-[#FAFAFA] text-[#52525B] shadow-sm hover:shadow-md hover:bg-white transition-all"
              title="Re-fetch profile metadata (uses cached scrape if <6mo old)"
              data-testid="re-enrich-btn"
            >
              <Sparkle size={10} weight="bold" /> RE-ENRICH
            </button>
            <button
              onClick={forceScrape}
              className="h-7 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 rounded-md bg-[#FAFAFA] text-[#52525B] shadow-sm hover:shadow-md hover:bg-white transition-all"
              title="Force refresh of the official-site scrape (ignores 6-month cache)"
              data-testid="force-scrape-btn"
            >
              <ArrowsClockwise size={10} weight="bold" /> FORCE SCRAPE
            </button>
            <button
              onClick={openEditPanel}
              className="h-7 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 rounded-md bg-[#6043EF] text-white shadow-sm hover:shadow-md hover:bg-[#4e35c9] transition-all"
              title="Manually correct location, official site, established year, type, deemed status, affiliate university or phone"
              data-testid="edit-college-btn"
            >
              <PencilSimple size={10} weight="bold" /> EDIT COLLEGE
            </button>
            <button
              onClick={openVerifyDeemed}
              className="h-7 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 rounded-md bg-[#FAFAFA] text-[#52525B] shadow-sm hover:shadow-md hover:bg-white transition-all"
              title="Website-grounded check of Deemed status"
              data-testid="verify-deemed-btn"
            >
              <SealCheck size={10} weight="bold" /> VERIFY DEEMED
            </button>
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          {college.college_code && (
            <span className="font-['IBM_Plex_Mono'] text-xs font-bold text-[#6043EF]">{college.college_code}</span>
          )}
          <span className="flex items-center gap-1 text-sm text-[#52525B]">
            <MapPin size={14} /> {college.city || college.location}{college.state ? `, ${college.state}` : ""}
          </span>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${TYPE_BADGE[college.college_type]}`}>
            {college.college_type}
          </Badge>
          {college.college_type && <SourceChip chip={chipForField(college.field_sources, "college_type")} />}
          {college.college_status && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-zinc-400 text-[#52525B]" data-testid="college-status-badge">
              {college.college_status}
            </Badge>
          )}
          {college.college_status && <SourceChip chip={chipForField(college.field_sources, "college_status")} />}
          {college.national_importance && (
            <Badge className="text-[10px] uppercase tracking-wider bg-[#6043EF] text-white flex items-center gap-1" data-testid="national-importance-badge">
              <SealCheck size={11} weight="fill" /> National Importance
            </Badge>
          )}
          <span className="text-xs text-[#52525B]">Counselling: {college.counselling_body}</span>
          {college.official_website && (
            <a
              href={college.official_website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-bold text-[#6043EF] hover:underline"
              data-testid="college-website-link"
            >
              <ArrowSquareOut size={13} weight="bold" /> Official Website
            </a>
          )}
          {college.official_website && <SourceChip chip={chipForField(college.field_sources, "official_website")} />}
          {college.phone && (
            <a
              href={`tel:${college.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-1 text-xs font-bold text-[#6043EF] hover:underline"
              data-testid="college-call-link"
            >
              <Phone size={13} weight="bold" /> {college.phone}
            </a>
          )}
          {college.phone && <SourceChip chip={chipForField(college.field_sources, "phone")} />}
          {college.google_rating != null && (
            <span
              className="flex items-center gap-1 text-xs font-bold text-[#09090B] bg-[#FBBF24]/20 px-2 py-0.5 rounded-sm"
              data-testid="college-google-rating"
              title="Google rating"
            >
              <Star size={12} weight="fill" className="text-[#F59E0B]" />
              {college.google_rating}
              {college.google_rating_count > 0 && (
                <span className="text-[10px] font-normal text-[#52525B]">
                  ({college.google_rating_count.toLocaleString()})
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="college-detail-page">
      {/* Campus image lightbox */}
      {lightboxIndex !== null && imagesCount > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200"
          onClick={closeLightbox}
          data-testid="campus-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white transition-colors z-10"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={28} weight="bold" />
          </button>

          {imagesCount > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 sm:p-3 transition-colors z-10"
              aria-label="Previous image"
              data-testid="lightbox-prev"
            >
              <CaretLeft size={24} weight="bold" />
            </button>
          )}

          <div className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              key={lightboxIndex}
              src={`${ASSET_BASE}/colleges/${college.college_code}/images/${lightboxIndex}`}
              alt={`Campus ${lightboxIndex + 1}`}
              className="max-w-[90vw] max-h-[80vh] object-contain animate-in fade-in zoom-in-95 duration-200"
              data-testid="lightbox-image"
            />
            <div className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-white/60" data-testid="lightbox-counter">
              {lightboxIndex + 1} / {imagesCount}
            </div>
          </div>

          {imagesCount > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 sm:p-3 transition-colors z-10"
              aria-label="Next image"
              data-testid="lightbox-next"
            >
              <CaretRight size={24} weight="bold" />
            </button>
          )}
        </div>
      )}

      {/* ─── Combined hero banner + info overlay ──────────────────────────
          When the college has a header image, we stack the banner and the
          info panel into a single tall section. The image fills the whole
          section, and the info panel sits at the bottom as a frosted-white
          overlay. When there's no header image, we degrade to the original
          light-gray solid header (no visual regression). */}
      {college.has_header ? (
        <section className="relative w-full min-h-[16rem] sm:min-h-[20rem] lg:min-h-[24rem] flex flex-col justify-end overflow-hidden bg-zinc-200" data-testid="college-hero">
          <img
            src={`${ASSET_BASE}/colleges/${college.college_code}/header${college.header_version ? `?v=${college.header_version}` : ""}`}
            alt={`${college.short_name || college.name} building`}
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          {/* Light gradient scrim anchored to the BOTTOM only: the top of the
              image stays fully clear, white builds up just behind the text zone
              (BACK TO COLLEGES → bottom) so the info stays legible. */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent via-red-100/60 to-red-200/90 pointer-events-none" />

          {/* Info sits directly over the fade — no white panel, no blur. */}
          <div className="relative z-[1] border-b border-zinc-200/60">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-6 pb-4">
              <Link to="/colleges" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.15em] text-[#3F3F46] hover:text-[#6043EF] transition-colors mb-3 bg-white/70 hover:bg-white/90 backdrop-blur-sm rounded-full px-3.5 py-1.5 shadow-sm border border-white/60" data-testid="back-to-colleges">
                <ArrowLeft size={13} weight="bold" /> BACK TO COLLEGES
              </Link>
              {renderHeaderContent()}
            </div>
          </div>
        </section>
      ) : (
        <div className="border-b border-zinc-200 bg-[#F4F4F5]" data-testid="college-hero-fallback">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
            <Link to="/colleges" className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] hover:text-[#6043EF] transition-colors mb-3" data-testid="back-to-colleges">
              <ArrowLeft size={12} /> BACK TO COLLEGES
            </Link>
            {renderHeaderContent()}
          </div>
        </div>
      )}

      {/* Aliases Panel — collapsible */}
      {showAliases && (
        <div className="border-b border-zinc-200 bg-[#6043EF]/5 animate-fade-in" data-testid="aliases-panel">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={14} weight="bold" className="text-[#6043EF]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">KNOWN NAMES & CODES</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Names */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">NAMES</span>
                  {isAdmin && (
                    <button
                      onClick={() => setAddAlias((s) => ({ ...s, open: !s.open }))}
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#6043EF] text-white hover:bg-[#4d35c9] transition-colors"
                      title="Add a name alias"
                      data-testid="add-alias-toggle">
                      <Plus size={10} weight="bold" />
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => setSplitOpen(true)}
                      className="inline-flex items-center gap-1 h-4 px-1.5 rounded-full bg-indigo-100 text-[#6043EF] hover:bg-indigo-200 transition-colors text-[8px] font-bold uppercase tracking-wider"
                      title="Split a wrongly-merged college out into a new master"
                      data-testid="split-master-toggle">
                      <ArrowsSplit size={10} weight="bold" /> Split
                    </button>
                  )}
                </div>
                {isAdmin && addAlias.open && (
                  <div className="flex items-center gap-2 mb-2" data-testid="add-alias-form">
                    <Input
                      value={addAlias.value}
                      onChange={(e) => setAddAlias((s) => ({ ...s, value: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && submitAddAlias()}
                      placeholder="New name / alias"
                      className="h-7 text-xs"
                      data-testid="add-alias-input" />
                    <Button
                      size="sm"
                      onClick={submitAddAlias}
                      disabled={addAlias.saving || !addAlias.value.trim()}
                      className="h-7 px-3 text-[10px] bg-[#6043EF] hover:bg-[#4d35c9]"
                      data-testid="add-alias-submit">
                      {addAlias.saving ? "…" : "ADD"}
                    </Button>
                  </div>
                )}
                <div className="space-y-1">
                  {college.short_name && (
                    <div className="flex items-center gap-2 text-xs">
                      <Badge
                        onClick={isAdmin ? () => applyDisplayName(college.short_name, "SHORT") : undefined}
                        title={isAdmin ? "Set as the college's display name" : undefined}
                        data-testid="set-display-short"
                        className={`bg-[#09090B] text-white text-[8px] ${isAdmin ? "cursor-pointer hover:opacity-80" : ""} ${(college.display_name || "").trim() === college.short_name.trim() ? "ring-2 ring-offset-1 ring-emerald-500" : ""} ${settingDisplay ? "pointer-events-none opacity-60" : ""}`}>SHORT</Badge>
                      <span className="font-sans">{college.short_name}</span>
                    </div>
                  )}
                  {college.name && college.name !== college.short_name && (
                    <div className="flex items-center gap-2 text-xs">
                      <Badge
                        onClick={isAdmin ? () => applyDisplayName(college.name, "PRIMARY") : undefined}
                        title={isAdmin ? "Set as the college's display name" : undefined}
                        data-testid="set-display-primary"
                        className={`bg-[#52525B] text-white text-[8px] ${isAdmin ? "cursor-pointer hover:opacity-80" : ""} ${(college.display_name || "").trim() === (college.name || "").trim() ? "ring-2 ring-offset-1 ring-emerald-500" : ""} ${settingDisplay ? "pointer-events-none opacity-60" : ""}`}>PRIMARY</Badge>
                      <span className="font-sans text-[#52525B]">{college.name}</span>
                    </div>
                  )}
                  {college.nmc_data?.name && (
                    <div className="flex items-center gap-2 text-xs" data-testid="nmc-name-row">
                      <Badge
                        onClick={isAdmin ? () => applyDisplayName(college.nmc_data.name, "NMC") : undefined}
                        title={isAdmin ? "Set as the college's display name" : undefined}
                        data-testid="set-display-nmc"
                        className={`bg-[#6043EF] text-white text-[8px] ${isAdmin ? "cursor-pointer hover:opacity-80" : ""} ${(college.display_name || "").trim() === (college.nmc_data.name || "").trim() ? "ring-2 ring-offset-1 ring-emerald-500" : ""} ${settingDisplay ? "pointer-events-none opacity-60" : ""}`}>NMC</Badge>
                      <span className="font-sans">{college.nmc_data.name}</span>
                      {isAdmin && (
                        <button
                          onClick={removeNmcName}
                          disabled={clearingNmcName}
                          className="text-rose-500 hover:text-rose-700 disabled:opacity-40 transition-colors"
                          title={`Clear this NMC name from ${college.short_name || college.name || code}`}
                          data-testid="remove-nmc-name">
                          <X size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  )}
                  {(college.aliases || []).map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs group" data-testid={`alias-row-${i}`}>
                      <Badge
                        variant="outline"
                        onClick={isAdmin ? () => applyDisplayName(a.name, (a.source || "alias").replace(/_/g, " ").toUpperCase()) : undefined}
                        title={isAdmin ? "Set as the college's display name" : undefined}
                        data-testid={`set-display-alias-${i}`}
                        className={`text-[8px] border-zinc-400 text-[#52525B] ${isAdmin ? "cursor-pointer hover:border-[#6043EF] hover:text-[#6043EF]" : ""} ${(college.display_name || "").trim() === (a.name || "").trim() ? "ring-2 ring-offset-1 ring-emerald-500" : ""} ${settingDisplay ? "pointer-events-none opacity-60" : ""}`}>{(a.source || "alias").replace(/_/g, " ").toUpperCase()}</Badge>
                      <span className="font-sans">{a.name}</span>
                      {a.city && <span className="text-[10px] text-zinc-400">({a.city})</span>}
                      {isAdmin && (
                        <button
                          onClick={() => removeAlias(a, i)}
                          disabled={removingAlias === i}
                          className="text-rose-500 hover:text-rose-700 disabled:opacity-40 transition-colors"
                          title={`Remove this alias from ${college.short_name || college.name || code}`}
                          data-testid={`remove-alias-${i}`}>
                          <X size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* Codes */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">CODES</span>
                  {isAdmin && (
                    <button
                      onClick={() => setAddCode((s) => ({ ...s, open: !s.open }))}
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#6043EF] text-white hover:bg-[#4d35c9] transition-colors"
                      title="Add a code"
                      data-testid="add-code-toggle">
                      <Plus size={10} weight="bold" />
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => setMerge((s) => ({ ...s, open: true }))}
                      className="inline-flex items-center gap-1 h-4 px-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors text-[8px] font-bold uppercase tracking-wider"
                      title="Merge another (same-state) college's data into this one"
                      data-testid="merge-master-toggle">
                      <ArrowsMerge size={10} weight="bold" /> Merge
                    </button>
                  )}
                </div>
                {isAdmin && addCode.open && (
                  <div className="flex items-center gap-2 mb-2" data-testid="add-code-form">
                    <Input
                      value={addCode.body}
                      onChange={(e) => setAddCode((s) => ({ ...s, body: e.target.value }))}
                      placeholder="Body (e.g. NMC)"
                      className="h-7 text-xs w-24 font-['IBM_Plex_Mono']"
                      data-testid="add-code-body-input" />
                    <Input
                      value={addCode.value}
                      onChange={(e) => setAddCode((s) => ({ ...s, value: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && submitAddCode()}
                      placeholder="Code"
                      className="h-7 text-xs flex-1 font-['IBM_Plex_Mono']"
                      data-testid="add-code-value-input" />
                    <Button
                      size="sm"
                      onClick={submitAddCode}
                      disabled={addCode.saving || !addCode.body.trim() || !addCode.value.trim()}
                      className="h-7 px-3 text-[10px] bg-[#6043EF] hover:bg-[#4d35c9]"
                      data-testid="add-code-submit">
                      {addCode.saving ? "…" : "ADD"}
                    </Button>
                  </div>
                )}
                <div className="space-y-1">
                  {college.codes && Object.entries(college.codes).map(([body, c]) => (
                    <div key={body} className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="text-[8px] font-['IBM_Plex_Mono']">{body}</Badge>
                      <span className="font-['IBM_Plex_Mono'] font-bold">{c}</span>
                    </div>
                  ))}
                </div>
                {isAdmin && internalCodes?.codes?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-100" data-testid="internal-codes">
                    <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1">Internal codes · click to make primary</div>
                    <div className="flex flex-wrap gap-1.5">
                      {internalCodes.codes.map((ic) => (
                        <button
                          key={ic.code}
                          disabled={ic.is_canonical || switchingCode}
                          onClick={() => makePrimaryCode(ic.code, ic)}
                          title={ic.is_canonical ? "Current primary college_code" : `Make ${ic.code} the primary code — re-points ${ic.total} row(s)`}
                          data-testid={`internal-code-${ic.code}`}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-['IBM_Plex_Mono'] text-[10px] font-bold transition-colors ${ic.is_canonical ? "bg-emerald-600 text-white cursor-default" : "bg-zinc-100 text-zinc-700 hover:bg-[#6043EF] hover:text-white cursor-pointer"} ${switchingCode === ic.code ? "opacity-60 pointer-events-none" : ""}`}>
                          {ic.code}
                          {ic.is_canonical ? <span className="text-[7px] opacity-90">PRIMARY</span> : <span className="text-[8px] opacity-70">×{ic.total}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6">
        {/* 2-column split on lg+: main content (left) + sticky sidebar (right). */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 lg:gap-6">
          {/* ─── LEFT / MAIN COLUMN ─────────────────────────────────────── */}
          <div className="grid grid-cols-12 gap-4 min-w-0">
          {/* Eligibility + Priority Callback — moved from sidebar to main column,
              positioned where CollegeNews used to be. Displayed as two side-by-side
              cards on md+ so they don't dominate the page. */}
          <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4 items-start" data-testid="lead-and-predictor-row">
            <EligibilityPredictor college={college} cutoffs={cutoffs} />
            <LeadCallbackCard college={college} />
          </div>

          {/* Info Cards */}
          <div className="col-span-12 grid grid-cols-2 sm:grid-cols-4 border border-zinc-200" data-testid="college-info-grid">
            <InfoCell icon={CurrencyInr} label="ANNUAL FEES" value={formatFees((() => {
              // Latest govt fees from fees_csv_data, fallback to annual_fees
              if (college.fees_csv_data) {
                const latestYear = Object.keys(college.fees_csv_data).sort().reverse()[0];
                if (latestYear) {
                  const courses = college.fees_csv_data[latestYear];
                  const firstCourse = Object.values(courses)[0];
                  if (firstCourse?.govt_fees) return firstCourse.govt_fees;
                }
              }
              return college.annual_fees;
            })())} />
            <InfoCell icon={Buildings} label="TOTAL SEATS" value={college.total_seats} hint={college.seats_info?.note} verified={seatMatrixReconciled(seat_matrix, college.nmc_data?.seats)} />
            <InfoCell icon={CalendarBlank} label="ESTABLISHED" value={college.established_year || "N/A"} sourceChip={college.established_year ? chipForField(college.field_sources, "established_year") : null} />
            <InfoCell icon={Globe} label="OTHER DOMICILE" value={college.allows_other_domicile ? "ALLOWED" : "NOT ALLOWED"} noBorder />
          </div>

          {/* College Profile — enriched metadata */}
          {(college.affiliate_university || college.rank_highlight || college.college_status || college.domicile_note || college.enriched_at) && (
            <div className="col-span-12 border border-zinc-200" data-testid="college-profile">
              <div className="p-3 bg-[#6043EF]/5 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
                <GraduationCap size={14} weight="bold" className="text-[#6043EF]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">COLLEGE PROFILE</span>
                {college.enriched_at && (
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-['IBM_Plex_Mono'] text-zinc-400" data-testid="profile-updated-at">
                    <ClockCounterClockwise size={11} /> UPDATED {String(college.enriched_at).slice(0, 4)}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                {college.affiliate_university && (
                  <ProfileRow icon={GraduationCap} label="AFFILIATE UNIVERSITY" value={college.affiliate_university} testid="profile-affiliate" sourceChip={chipForField(college.field_sources, "affiliate_university")} />
                )}
                {college.rank_highlight && (
                  <ProfileRow icon={Trophy} label="RANK HIGHLIGHT" value={college.rank_highlight} testid="profile-rank" sourceChip={chipForField(college.field_sources, "rank_highlight")} />
                )}
                {college.college_status && (
                  <ProfileRow icon={Certificate} label="COLLEGE STATUS" value={college.college_status} testid="profile-status" sourceChip={chipForField(college.field_sources, "college_status")} />
                )}
                {college.domicile_note && (
                  <ProfileRow icon={Globe} label="DOMICILE NOTE" value={college.domicile_note} testid="profile-domicile-note" sourceChip={chipForField(college.field_sources, "domicile_note")} />
                )}
              </div>
            </div>
          )}

          {/* College Highlights */}
          {college.highlights?.length > 0 && (
            <div className="col-span-12 lg:col-span-7 self-start border border-zinc-200" data-testid="college-highlights">
              <div className="p-3 bg-[#6043EF]/5 border-b border-zinc-200 flex items-center gap-2">
                <CheckCircle size={14} weight="bold" className="text-[#6043EF]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">COLLEGE HIGHLIGHTS</span>
              </div>
              <ul className="p-4 space-y-2">
                {college.highlights.map((h, idx) => {
                  // Backward-compat: highlights may be a plain string (legacy) or {text, source} (new).
                  const text   = typeof h === "string" ? h : (h?.text || "");
                  const source = typeof h === "string" ? null : h?.source;
                  const chip = HL_SOURCE_CHIPS[source];
                  return (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#09090B]" data-testid={`highlight-${idx}`}>
                      <CheckCircle size={15} weight="fill" className="text-[#00C04B] mt-0.5 shrink-0" />
                      <span className="flex-1">
                        {text}
                        {chip && (
                          <span
                            className={`inline-block ml-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full align-middle ${chip.bg} ${chip.fg}`}
                            title={chip.title}
                          >
                            {chip.label}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Courses Offered (from historical cutoffs / allotments) */}
          {college.courses?.length > 0 && (
            <div className="col-span-12 lg:col-span-5 self-start border border-zinc-200" data-testid="college-courses">
              <div className="p-3 bg-[#6043EF]/5 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
                <GraduationCap size={14} weight="bold" className="text-[#6043EF]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">COURSES</span>
                {college.field_sources?.courses && (
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400" data-testid="courses-source">
                    SOURCE <SourceChip chip={chipForField(college.field_sources, "courses")} />
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {college.courses.map((c, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs font-semibold text-[#09090B] bg-zinc-100 border border-zinc-200 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors"
                      data-testid={`course-${idx}`}
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-zinc-400">Seen in past counselling allotments</p>
              </div>
            </div>
          )}

          {/* Campus Images moved to the right sidebar (renders once per page). */}

          {/* NMC Approved Data */}
          {college.nmc_data?.seats && (
            <div className="col-span-12 border border-zinc-200" data-testid="nmc-data">
              <div className="p-3 bg-[#6043EF]/5 border-b border-zinc-200 flex items-center gap-2">
                <Buildings size={14} weight="bold" className="text-[#6043EF]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">NMC APPROVED SEATS</span>
                {college.nmc_data.management && (
                  <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-[#6043EF] text-[#6043EF]">{college.nmc_data.management}</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-0">
                {Object.entries(college.nmc_data.seats)
                  .sort(([a], [b]) => parseInt(b) - parseInt(a))
                  .map(([yr, data]) => {
                    const seats = data?.mbbs || data?.bds || 0;
                    const pubDate = data?.pub_date || "";
                    return (
                      <div key={yr} className="p-4 border-r border-b border-zinc-200">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] block mb-1">
                          AY {yr}-{(parseInt(yr) + 1) % 100 || "00"}
                        </span>
                        <span className="font-['IBM_Plex_Mono'] text-2xl font-bold text-[#09090B]">{seats}</span>
                        {pubDate && (
                          <span className="block text-[10px] font-['IBM_Plex_Mono'] text-zinc-400 mt-1">as of {pubDate}</span>
                        )}
                        {data?.remarks && (
                          <span className="block text-[10px] text-[#FF2A00] mt-0.5">{data.remarks}</span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Fees Structure from CSV */}
          {college.fees_csv_data && Object.keys(college.fees_csv_data).length > 0 && (
            <div className="col-span-12 border border-zinc-200" data-testid="fees-csv-data">
              <div className="p-3 bg-[#00C04B]/5 border-b border-zinc-200 flex items-center gap-2">
                <CurrencyInr size={14} weight="bold" className="text-[#00C04B]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C04B]">FEES STRUCTURE</span>
              </div>
              {(() => {
                const feeYears = Object.keys(college.fees_csv_data).sort((a, b) => b.localeCompare(a));
                const renderCourses = (yearKey, courses) => (
                  Object.entries(courses).map(([course, data]) => (
                    <FeeCourseBlock key={`${yearKey}-${course}`} yearKey={yearKey} course={course} data={data}
                      code={code} isAdmin={isAdmin} showInr={showInr} setShowInr={setShowInr} usdRate={usdRate} onChanged={loadCollege} />
                  ))
                );
                if (feeYears.length === 1) return renderCourses(feeYears[0], college.fees_csv_data[feeYears[0]]);
                return (
                  <Tabs defaultValue={feeYears[0]} className="w-full">
                    <TabsList className="w-full justify-start bg-[#F4F4F5] border-b border-zinc-200 rounded-none h-9 p-0 gap-0">
                      {feeYears.map((yr) => (
                        <TabsTrigger
                          key={yr}
                          value={yr}
                          className="rounded-none text-[10px] font-bold uppercase tracking-[0.15em] px-5 h-full data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] data-[state=active]:text-[#6043EF]"
                          data-testid={`fees-csv-year-tab-${yr}`}
                        >
                          AY {yr}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {feeYears.map((yr) => (
                      <TabsContent key={yr} value={yr} className="mt-0">
                        {renderCourses(yr, college.fees_csv_data[yr])}
                      </TabsContent>
                    ))}
                  </Tabs>
                );
              })()}
            </div>
          )}

          {/* Fees by Quota — tabbed by year */}
          {college.fees_by_year && Object.keys(college.fees_by_year).length > 0 && (
            <div className="col-span-12 border border-zinc-200" data-testid="fees-by-quota">
              <div className="p-3 bg-[#F4F4F5] border-b border-zinc-200">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">FEES BY QUOTA TYPE</span>
              </div>
              {Object.keys(college.fees_by_year).length === 1 ? (
                <FeeQuotaGrid quotas={Object.values(college.fees_by_year)[0]} />
              ) : (
                <Tabs defaultValue={Math.max(...Object.keys(college.fees_by_year).map(Number)).toString()} className="w-full">
                  <TabsList className="w-full justify-start bg-[#F4F4F5] border-b border-zinc-200 rounded-none h-9 p-0 gap-0">
                    {Object.keys(college.fees_by_year).sort((a, b) => Number(b) - Number(a)).map((yr) => (
                      <TabsTrigger
                        key={yr}
                        value={yr}
                        className="rounded-none text-[10px] font-bold uppercase tracking-[0.15em] px-5 h-full data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] data-[state=active]:text-[#6043EF]"
                        data-testid={`fee-year-tab-${yr}`}
                      >
                        {yr}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {Object.entries(college.fees_by_year).map(([yr, quotas]) => (
                    <TabsContent key={yr} value={yr} className="mt-0">
                      <FeeQuotaGrid quotas={quotas} />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="col-span-12" data-testid="college-tabs">
            <Tabs defaultValue="seat-matrix" className="w-full">
              <TabsList className="w-full justify-start bg-[#F4F4F5] border border-zinc-200 rounded-none h-10 p-0">
                <TabsTrigger value="seat-matrix" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] h-full px-6" data-testid="tab-seat-matrix">
                  SEAT MATRIX
                </TabsTrigger>
                <TabsTrigger value="cutoffs" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] h-full px-6" data-testid="tab-cutoffs">
                  HISTORICAL CUTOFFS
                </TabsTrigger>
              </TabsList>

              <TabsContent value="seat-matrix" className="mt-0">
                {seat_matrix && seat_matrix.length > 0 ? (
                  <SeatMatrixByYear matrices={seat_matrix} nmcSeats={college.nmc_data?.seats} isAdmin={isAdmin} code={college.college_code || code} />
                ) : (
                  <div className="border border-zinc-200 border-t-0 p-8 text-center">
                    <p className="text-sm text-[#52525B]">No seat matrix data available</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="cutoffs" className="mt-0">
                {Object.keys(cutoffsByYear).length > 0 ? (
                  <div className="border border-zinc-200 border-t-0 overflow-x-auto" data-testid="cutoffs-table">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">YEAR</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">ROUND</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">BODY</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">QUOTA</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">CATEGORY</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right" title="Closing rank on the NEET All-India Rank basis (merit-backed for state bodies once their rank list is ingested)">CLOSING RANK (AIR)</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right" title="Closing rank on the state/KEAM merit-rank basis (shown for state-rank bodies like Kerala/Jharkhand)">STATE RANK</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">FEES / YR</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">COURSE</TableHead>
                        </TableRow>
                        <TableRow className="bg-white hover:bg-white border-b border-zinc-200">
                          <TableHead className="py-2"><CutoffFilter value={cutoffFilters.year} onChange={(v) => setCF("year", v)} options={cutoffOptions.year.map((y) => ({ value: String(y), label: String(y) }))} placeholder="All" testid="cf-year" /></TableHead>
                          <TableHead className="py-2"><CutoffFilter value={cutoffFilters.round} onChange={(v) => setCF("round", v)} options={cutoffOptions.round.map((r) => ({ value: String(r), label: formatRound(r) }))} placeholder="All" testid="cf-round" /></TableHead>
                          <TableHead className="py-2"><CutoffFilter value={cutoffFilters.body} onChange={(v) => setCF("body", v)} options={cutoffOptions.body.map((b) => ({ value: b, label: b === "MCC" ? "MCC" : b.replace(/_/g, " ") }))} placeholder="All" testid="cf-body" /></TableHead>
                          <TableHead className="py-2"><CutoffFilter value={cutoffFilters.quota} onChange={(v) => setCF("quota", v)} options={cutoffOptions.quota.map((q) => ({ value: q, label: q }))} placeholder="All" testid="cf-quota" /></TableHead>
                          <TableHead className="py-2"><CutoffMultiFilter selected={cutoffFilters.category} onToggle={toggleCategory} onClear={() => setCF("category", [])} options={cutoffOptions.category} testid="cf-category" /></TableHead>
                          <TableHead className="py-2 text-right">
                            {cfActive && (
                              <button onClick={resetCF} className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF] hover:underline" data-testid="cf-reset">Clear</button>
                            )}
                          </TableHead>
                          <TableHead className="py-2"></TableHead>
                          <TableHead className="py-2"></TableHead>
                          <TableHead className="py-2"><CutoffFilter value={cutoffFilters.course} onChange={(v) => setCF("course", v)} options={cutoffOptions.course.map((c) => ({ value: c, label: c }))} placeholder="All" testid="cf-course" /></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCutoffs.map((c, i) => (
                          <TableRow key={i} className="hover:bg-zinc-50" data-testid={`cutoff-row-${i}`}>
                            <TableCell className="font-['IBM_Plex_Mono'] text-sm">{c.year}</TableCell>
                            <TableCell className="font-['IBM_Plex_Mono'] text-sm">{formatRound(c.round_number)}</TableCell>
                            <TableCell>
                              <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${c.counselling_body === "MCC" ? "bg-[#6043EF] text-white" : "bg-zinc-200 text-[#52525B]"}`}>
                                {c.counselling_body === "MCC" ? "MCC" : (c.counselling_body || "State").replace(/_/g, " ")}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-[#52525B]" data-testid={`cutoff-quota-${i}`}>{c.quota || "—"}</TableCell>
                            <TableCell>
                              <Badge className={`${CATEGORY_COLORS[c.category] || ""} text-[10px] uppercase tracking-wider`}>{c.category}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-['IBM_Plex_Mono'] font-semibold text-sm" data-testid={`cutoff-air-${i}`}>
                              {c.allotted_rank != null ? c.allotted_rank.toLocaleString() : <span className="text-zinc-400">—</span>}
                            </TableCell>
                            <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm text-[#52525B]" data-testid={`cutoff-state-${i}`}>
                              {c.state_rank != null ? c.state_rank.toLocaleString() : <span className="text-zinc-400">—</span>}
                            </TableCell>
                            <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm" data-testid={`cutoff-fee-${i}`}>
                              {c.fee_amount != null ? (
                                <div className="flex items-center justify-end gap-1.5">
                                  <span className="text-[#09090B]">{formatFeeShort(c.fee_amount, c.fee_currency)}</span>
                                  {c.fee_type && (
                                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${FEE_BUCKET_CHIP[c.fee_bucket] || "bg-zinc-100 text-zinc-600"}`} title={`Fee bucket: ${c.fee_type}`}>
                                      {c.fee_type}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-zinc-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-[#52525B]">{c.course}</TableCell>
                          </TableRow>
                        ))}
                        {filteredCutoffs.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center text-sm text-[#52525B] py-6" data-testid="cutoffs-empty">
                              No cutoffs match the selected filters.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="border border-zinc-200 border-t-0 p-8 text-center">
                    <p className="text-sm text-[#52525B]">No cutoff data available</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
          </div>{/* /LEFT / MAIN COLUMN */}

          {/* ─── RIGHT / STICKY SIDEBAR ─────────────────────────────────── */}
          <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start min-w-0" data-testid="college-detail-sidebar">
            {/* Campus photos (moved from main content) */}
            {college.images_count > 0 && (
              <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white" data-testid="college-gallery">
                <div className="p-3 bg-[#6043EF]/5 border-b border-zinc-200 flex items-center gap-2">
                  <ImageSquare size={14} weight="bold" className="text-[#6043EF]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">CAMPUS · {college.images_count} PHOTOS</span>
                </div>
                <div className="p-3 grid grid-cols-2 gap-2">
                  {Array.from({ length: Math.min(college.images_count, 6) }).map((_, idx) => (
                    <div
                      key={idx}
                      className="relative group aspect-square overflow-hidden rounded-lg border border-zinc-200 hover:border-[#6043EF] transition-colors"
                      data-testid={`campus-image-${idx}`}
                    >
                      <img
                        src={`${ASSET_BASE}/colleges/${college.college_code}/images/${idx}`}
                        alt={`Campus ${idx + 1}`}
                        loading="lazy"
                        decoding="async"
                        onClick={() => setLightboxIndex(idx)}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                        onError={(e) => { e.target.closest('div').style.display = 'none'; }}
                      />
                      {college.banner_idx === idx ? (
                        <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 bg-[#6043EF] text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" data-testid={`banner-badge-${idx}`}>
                          <Star size={9} weight="fill" /> Banner
                        </span>
                      ) : isAdmin ? (
                        <button
                          type="button"
                          onClick={() => handleSetBanner(idx)}
                          disabled={settingBanner !== null}
                          className="absolute bottom-1 left-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 hover:bg-[#6043EF] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center justify-center gap-1 disabled:opacity-40"
                          data-testid={`set-banner-${idx}`}
                        >
                          {settingBanner === idx ? "…" : (<><Star size={10} weight="regular" /> Banner</>)}
                        </button>
                      ) : null}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(idx)}
                          disabled={deletingImage !== null}
                          title="Delete this campus photo"
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 hover:bg-rose-600 text-rose-600 hover:text-white shadow-sm w-6 h-6 rounded-full flex items-center justify-center disabled:opacity-40"
                          data-testid={`delete-image-${idx}`}
                        >
                          {deletingImage === idx ? "…" : <Trash size={12} weight="bold" />}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {college.images_count > 6 && (
                  <div className="px-3 pb-3 text-center">
                    <button onClick={() => setLightboxIndex(0)} className="text-[11px] font-bold uppercase tracking-wider text-[#6043EF] hover:underline" data-testid="view-all-campus">
                      View all {college.images_count} photos →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Latest news — sidebar variant (vertically stacked) */}
            <CollegeNews
              code={college.college_code}
              name={college.display_name || college.short_name || college.primary_name || college.college_code}
              variant="sidebar"
            />
          </aside>
        </div>{/* /2-col split */}
      </div>

      <Dialog open={reEnrich.open} onOpenChange={(o) => !reEnrich.applying && setReEnrich((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="re-enrich-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Sparkle size={16} weight="bold" className="text-[#6043EF]" /> Re-enrich profile
            </DialogTitle>
          </DialogHeader>
          {reEnrich.loading ? (
            <div className="py-8 text-center text-sm text-[#52525B]" data-testid="re-enrich-loading">Checking for updated data…</div>
          ) : reEnrich.changes && Object.keys(reEnrich.changes).length === 0 ? (
            <div className="py-6 text-center text-sm text-[#52525B]" data-testid="re-enrich-nochange">Everything is already up to date — no differences found.</div>
          ) : (
            <div className="space-y-3 max-h-[55vh] overflow-y-auto" data-testid="re-enrich-changes">
              <p className="text-xs text-[#52525B]">Tick the fields you want to update — untick any you disagree with. The logo is never changed.</p>
              {Object.entries(reEnrich.changes || {}).map(([field, c]) => (
                <div
                  key={field}
                  onClick={() => setReEnrich((p) => ({ ...p, selected: { ...p.selected, [field]: !p.selected[field] } }))}
                  className="flex gap-2 border border-zinc-200 p-2 cursor-pointer hover:bg-zinc-50"
                  data-testid={`re-enrich-change-${field}`}
                >
                  <Checkbox
                    checked={!!reEnrich.selected[field]}
                    className="mt-0.5 pointer-events-none"
                    data-testid={`re-enrich-check-${field}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF] mb-1">{field.replace(/_/g, " ")}</div>
                    <div className="text-xs text-[#B91C1C] line-through break-words whitespace-pre-wrap">{fmtDiffVal(c.current)}</div>
                    <div className="text-xs text-[#15803D] break-words whitespace-pre-wrap mt-0.5">{fmtDiffVal(c.proposed)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReEnrich((p) => ({ ...p, open: false }))} disabled={reEnrich.applying} data-testid="re-enrich-cancel">Cancel</Button>
            {!reEnrich.loading && reEnrich.changes && Object.keys(reEnrich.changes).length > 0 && (() => {
              const n = Object.values(reEnrich.selected).filter(Boolean).length;
              return (
                <Button onClick={applyReEnrich} disabled={reEnrich.applying || n === 0} className="bg-[#6043EF] hover:bg-[#4e35c9]" data-testid="re-enrich-apply">
                  {reEnrich.applying ? "Applying…" : `Apply ${n} change(s)`}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Master wizard */}
      <SplitMasterDialog open={splitOpen} onClose={() => setSplitOpen(false)} sourceCode={code} onDone={loadCollege} />

      {/* Merge Master modal */}
      <Dialog open={merge.open} onOpenChange={(o) => !merge.loading && setMerge((s) => ({ ...s, open: o, selected: o ? s.selected : null, preview: o ? s.preview : null, q: o ? s.q : "", results: o ? s.results : [] }))}>
        <DialogContent className="max-w-lg" data-testid="merge-master-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ArrowsMerge size={16} weight="bold" className="text-amber-600" /> Merge college into this one
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#52525B] bg-amber-50 border border-amber-200 rounded p-2">
              Data from the college you pick below (allotments, seat matrix, results, codes & aliases) will be moved into
              <span className="font-bold"> {college.short_name || college.primary_name} ({code})</span>, and the source college will be deleted. This is reversible with an <span className="font-bold">Undo</span>.
            </div>

            {/* Search source college */}
            {!merge.selected && (
              <div>
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                  <Input
                    autoFocus
                    value={merge.q}
                    onChange={(e) => runMergeSearch(e.target.value)}
                    placeholder={`Search a college in ${college.state || "this state"} to merge in…`}
                    className="h-9 text-sm pl-8"
                    data-testid="merge-search-input" />
                </div>
                <div className="mt-2 max-h-[40vh] overflow-y-auto divide-y divide-zinc-100 border border-zinc-200 rounded" data-testid="merge-search-results">
                  {merge.searching && <div className="p-3 text-xs text-[#52525B]">Searching…</div>}
                  {!merge.searching && merge.q.trim().length >= 2 && merge.results.length === 0 && (
                    <div className="p-3 text-xs text-[#52525B]">No matches found.</div>
                  )}
                  {merge.results.map((r) => {
                    const rc = r.college_code || r.code;
                    return (
                      <button
                        key={rc}
                        onClick={() => selectMergeSource(r)}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-50 transition-colors"
                        data-testid={`merge-result-${rc}`}>
                        <div className="text-sm font-semibold text-[#09090B] truncate">{r.short_name || r.primary_name}</div>
                        <div className="text-[10px] text-[#71717A] font-['IBM_Plex_Mono'] flex items-center gap-2">
                          <span>{rc}</span>
                          {r.city && <span>· {r.city}</span>}
                          {r.state && <span>· {r.state}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preview of selected source */}
            {merge.selected && (
              <div data-testid="merge-preview">
                {merge.loading && !merge.preview ? (
                  <div className="py-6 text-center text-sm text-[#52525B]">Loading preview…</div>
                ) : merge.preview?.ok ? (
                  <div className="space-y-3">
                    {merge.preview.blocked && (
                      <div className="text-xs text-[#B91C1C] bg-red-50 border border-red-200 rounded p-2 flex gap-2" data-testid="merge-blocked-banner">
                        <X size={14} weight="bold" className="shrink-0 mt-0.5" />
                        <span><span className="font-bold">Merge blocked.</span> {merge.preview.block_reason}</span>
                      </div>
                    )}
                    {!merge.preview.blocked && merge.preview.match?.verdict === "different" && (
                      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 flex gap-2" data-testid="merge-warn-banner">
                        <Sparkle size={14} weight="bold" className="shrink-0 mt-0.5" />
                        <span><span className="font-bold">These may be different colleges.</span> {merge.preview.match.reason} — you can still merge if you're sure.</span>
                      </div>
                    )}
                    {!merge.preview.blocked && merge.preview.match?.verdict === "same" && (
                      <div className="text-xs text-[#15803D] bg-green-50 border border-green-200 rounded p-2 flex gap-2" data-testid="merge-ok-banner">
                        <CheckCircle size={14} weight="bold" className="shrink-0 mt-0.5" />
                        <span>AI check: likely the same institution.{merge.preview.match.reason ? ` ${merge.preview.match.reason}` : ""}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className="text-[9px] font-['IBM_Plex_Mono']">{merge.preview.source.college_code}</Badge>
                      <span className="font-semibold truncate">{merge.preview.source.name}</span>
                      <ArrowsMerge size={14} className="text-amber-600 shrink-0" />
                      <span className="text-[#71717A] shrink-0">into {code}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {[
                        ["Allotments", merge.preview.counts.raw_allotments],
                        ["Seat-matrix rows", merge.preview.counts.seat_matrix_detail],
                        ["Results", merge.preview.counts.counselling_results],
                      ].map(([lbl, n]) => (
                        <div key={lbl} className="border border-zinc-200 rounded p-2" data-testid={`merge-count-${lbl}`}>
                          <div className="text-lg font-bold text-[#09090B]">{n}</div>
                          <div className="text-[9px] uppercase tracking-wider text-[#71717A]">{lbl}</div>
                        </div>
                      ))}
                    </div>
                    {(merge.preview.counts.codes?.length > 0 || merge.preview.counts.aliases?.length > 0 || merge.preview.counts.fees_years?.length > 0) && (
                      <div className="text-[11px] text-[#52525B] space-y-1">
                        {merge.preview.counts.codes?.length > 0 && <div><span className="font-bold">Codes:</span> {merge.preview.counts.codes.join(", ")}</div>}
                        {merge.preview.counts.fees_years?.length > 0 && <div><span className="font-bold">Fees years:</span> {merge.preview.counts.fees_years.join(", ")}</div>}
                        {merge.preview.counts.aliases?.length > 0 && <div className="line-clamp-2"><span className="font-bold">Aliases ({merge.preview.counts.aliases.length}):</span> {merge.preview.counts.aliases.join(", ")}</div>}
                      </div>
                    )}
                    <button
                      onClick={() => setMerge((s) => ({ ...s, selected: null, preview: null }))}
                      className="text-xs text-[#6043EF] hover:underline"
                      data-testid="merge-change-source">
                      ← Pick a different college
                    </button>
                  </div>
                ) : (
                  <div className="py-4 text-center text-sm text-[#B91C1C]">{merge.preview?.error || "Could not load preview."}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMerge((s) => ({ ...s, open: false }))} disabled={merge.loading} data-testid="merge-cancel">Cancel</Button>
            {merge.selected && merge.preview?.ok && !merge.preview.blocked && (
              <Button onClick={applyMerge} disabled={merge.loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="merge-apply">
                {merge.loading ? "Merging…" : "Merge & delete source"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={vd.open} onOpenChange={(o) => !vd.applying && setVd((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-md" data-testid="verify-deemed-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <SealCheck size={16} weight="bold" className="text-[#6043EF]" /> Verify Deemed status
            </DialogTitle>
          </DialogHeader>
          {vd.loading ? (
            <div className="py-8 text-center text-sm text-[#52525B]" data-testid="vd-loading">Checking against the official website…</div>
          ) : vd.data ? (
            <div className="space-y-3" data-testid="vd-result">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#52525B]">Current status:</span>
                <span className="font-bold text-[#09090B]">{vd.data.current_status}</span>
              </div>
              <div className="border border-zinc-200 p-3 text-xs text-[#52525B]">{vd.data.reason || "No specific evidence returned."}</div>
              {vd.data.recommended_action === "none" && !vd.data.affiliate_changed ? (
                <div className="text-sm text-[#15803D] font-semibold" data-testid="vd-nochange">✓ Status &amp; affiliate are correct — no change recommended.</div>
              ) : (
                <div className="space-y-2 text-sm" data-testid="vd-recommend">
                  {vd.data.recommended_action !== "none" && (
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF]">Status</span>
                      <div><span className="font-bold text-[#09090B] line-through">{vd.data.current_status}</span>
                        <span className="mx-1">→</span>
                        <span className="font-bold text-[#6043EF]">{vd.data.proposed_status}</span>
                        {vd.data.proposed_status === "Regular" && <span className="text-[10px] text-zinc-400 ml-1">(stale "deemed" text will be cleaned)</span>}
                      </div>
                    </div>
                  )}
                  {vd.data.affiliate_changed && (
                    <div data-testid="vd-affiliate">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF]">Affiliate University</span>
                      <div className="text-[#B91C1C] line-through break-words">{vd.data.current_affiliate || "—"}</div>
                      <div className="text-[#15803D] break-words">{vd.data.proposed_affiliate}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVd((p) => ({ ...p, open: false }))} disabled={vd.applying} data-testid="vd-cancel">Close</Button>
            {!vd.loading && vd.data && (vd.data.recommended_action !== "none" || vd.data.affiliate_changed) && (
              <Button onClick={applyVerifyDeemed} disabled={vd.applying} className="bg-[#6043EF] hover:bg-[#4e35c9]" data-testid="vd-apply">
                {vd.applying ? "Applying…" : "Apply correction"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={editPanel.open} onOpenChange={(o) => setEditPanel((p) => ({ ...p, open: o }))}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="edit-college-sheet">
          <SheetHeader>
            <SheetTitle>Edit College Details</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-5">
            {/* Location — manual edit + one-click AI sync (Gemini city + Places coords) */}
            <div className="space-y-2.5 rounded-lg border border-[#6043EF]/20 bg-[#6043EF]/[0.03] p-3" data-testid="edit-location-section">
              <div className="flex items-center justify-between gap-2">
                <Label className="flex items-center gap-1.5 text-[#6043EF]"><MapPin size={13} weight="bold" /> Location</Label>
                <button
                  type="button"
                  onClick={syncLocation}
                  disabled={locSync.busy}
                  data-testid="edit-location-ai-sync"
                  title="Fetch city via Gemini + coordinates via Google Places, then review before saving"
                  className="h-7 px-2.5 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 rounded-md bg-[#6043EF] text-white shadow-sm hover:bg-[#4e35c9] disabled:opacity-50 transition-all"
                >
                  <Sparkle size={11} weight="bold" /> {locSync.busy ? "Syncing…" : "AI Sync"}
                </button>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-city" className="text-[10px] text-zinc-500">City</Label>
                <Input
                  id="edit-city"
                  data-testid="edit-city-input"
                  placeholder="e.g. Pune"
                  value={editPanel.form.location || ""}
                  onChange={(e) => setCityField(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-state" className="text-[10px] text-zinc-500">State</Label>
                <Input
                  id="edit-state"
                  data-testid="edit-state-input"
                  list="edit-state-options"
                  autoComplete="off"
                  placeholder="e.g. Maharashtra"
                  value={editPanel.form.state || ""}
                  onChange={(e) => setEditField("state", e.target.value)}
                />
                <datalist id="edit-state-options">
                  {stateOptions.map((s) => <option key={s} value={s} />)}
                </datalist>
                {editPanel.form.state && stateOptions.length > 0 && !matchState(editPanel.form.state) && (
                  <p className="text-[10px] text-rose-600 font-medium" data-testid="edit-state-invalid">Not a valid state — pick one from the list.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] text-zinc-500">Coordinates</Label>
                  {locSource && (editPanel.form.lat || editPanel.form.lng) && (
                    <span
                      className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${GEO_SOURCE_STYLE[locSource] || "bg-zinc-100 text-zinc-500"}`}
                      data-testid="edit-geo-source"
                      title="Where these coordinates came from"
                    >
                      {locSource.replace(/[_-]/g, " ")}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id="edit-lat"
                    aria-label="Latitude"
                    data-testid="edit-lat-input"
                    placeholder="Lat (6 – 37.5)"
                    value={editPanel.form.lat || ""}
                    onChange={(e) => setCoordField("lat", e.target.value)}
                  />
                  <Input
                    id="edit-lng"
                    aria-label="Longitude"
                    data-testid="edit-lng-input"
                    placeholder="Lng (68 – 98)"
                    value={editPanel.form.lng || ""}
                    onChange={(e) => setCoordField("lng", e.target.value)}
                  />
                </div>
              </div>
              {locSync.done && <p className="text-[10px] text-emerald-600 font-medium">AI values filled in — edit if needed, then Save.</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-official-website">Official Website</Label>
              <Input
                id="edit-official-website"
                data-testid="edit-official-website-input"
                placeholder="e.g. skmch.in"
                value={editPanel.form.official_website || ""}
                onChange={(e) => setEditField("official_website", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-established-year">Established Year</Label>
              <Input
                id="edit-established-year"
                type="number"
                data-testid="edit-established-year-input"
                placeholder="e.g. 2019"
                value={editPanel.form.established_year || ""}
                onChange={(e) => setEditField("established_year", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-college-type">College Type</Label>
              <Select value={editPanel.form.college_type || ""} onValueChange={(v) => setEditField("college_type", v)}>
                <SelectTrigger id="edit-college-type" data-testid="edit-college-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="govt">Govt</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="deemed">Deemed</SelectItem>
                  <SelectItem value="trust">Trust</SelectItem>
                  <SelectItem value="central_govt">Central Govt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-college-status">Deemed Status</Label>
              <Select value={editPanel.form.college_status || ""} onValueChange={(v) => setEditField("college_status", v)}>
                <SelectTrigger id="edit-college-status" data-testid="edit-college-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Deemed">Deemed</SelectItem>
                  <SelectItem value="Autonomous">Autonomous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-affiliate">Affiliate University</Label>
              <Input
                id="edit-affiliate"
                data-testid="edit-affiliate-input"
                placeholder="e.g. RGUHS"
                value={editPanel.form.affiliate_university || ""}
                onChange={(e) => setEditField("affiliate_university", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                data-testid="edit-phone-input"
                placeholder="e.g. 080-12345678"
                value={editPanel.form.phone || ""}
                onChange={(e) => setEditField("phone", e.target.value)}
              />
            </div>
            <div className="space-y-1.5" data-testid="edit-courses-section">
              <div className="flex items-center gap-2">
                <Label>Courses</Label>
                <button
                  type="button"
                  onClick={() => setShowAddCourse((v) => !v)}
                  data-testid="edit-course-add-toggle"
                  title="Add a course from the seat-matrix / allotment catalog"
                  className={`flex items-center justify-center w-5 h-5 rounded-full transition-colors ${showAddCourse ? "bg-[#6043EF] text-white" : "bg-zinc-100 text-[#6043EF] hover:bg-[#6043EF]/10"}`}
                >
                  <Plus size={12} weight="bold" />
                </button>
              </div>
              {showAddCourse && (
                <div className="rounded-lg border border-[#6043EF]/20 bg-[#6043EF]/5 p-2 space-y-1.5" data-testid="edit-course-add-panel">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF]">Add from catalog (seat matrix + allotments)</p>
                  {(() => {
                    const available = (courseOptions || []).filter((c) => !(editPanel.form.courses || []).includes(c));
                    if (!courseOptions.length) return <p className="text-[11px] text-zinc-400 italic">Loading catalog…</p>;
                    if (!available.length) return <p className="text-[11px] text-zinc-400 italic">All catalog courses already added.</p>;
                    return (
                      <div className="flex flex-wrap gap-1.5">
                        {available.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => addCourseToForm(c)}
                            data-testid={`edit-course-option-${c}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-[#6043EF]/30 rounded-full text-[11px] font-medium text-[#6043EF] hover:bg-[#6043EF] hover:text-white transition-colors"
                          >
                            <Plus size={10} weight="bold" /> {c}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
              {(editPanel.form.courses || []).length === 0 ? (
                <p className="text-xs text-zinc-400 italic">No courses listed.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="edit-courses-list">
                  {(editPanel.form.courses || []).map((course, idx) => (
                    <span
                      key={`${course}-${idx}`}
                      data-testid={`edit-course-chip-${idx}`}
                      className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-zinc-100 border border-zinc-200 rounded-full text-xs font-medium text-zinc-700"
                    >
                      {course}
                      <button
                        type="button"
                        aria-label={`Remove ${course}`}
                        data-testid={`edit-course-remove-${idx}`}
                        onClick={() => removeCourseFromForm(idx)}
                        className="flex items-center justify-center w-4 h-4 rounded-full text-zinc-400 hover:text-white hover:bg-rose-500 transition-colors"
                      >
                        <X size={11} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-400">Remove any course that doesn't belong to this college, then Save.</p>
            </div>
            <p className="text-[10px] text-zinc-400">
              Any field you change here is saved exactly as typed and tagged with an <span className="font-bold text-rose-600">EDITOR</span> chip
              instead of AI/NMC/Official — it will not be overwritten by future enrichment or scraping runs.
            </p>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditPanel((p) => ({ ...p, open: false }))} disabled={editPanel.saving} data-testid="edit-college-cancel">
              Cancel
            </Button>
            <Button onClick={saveEditPanel} disabled={editPanel.saving} className="bg-[#6043EF] hover:bg-[#4e35c9]" data-testid="edit-college-save">
              {editPanel.saving ? "Saving…" : "Save changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Compact source-chip. Renders inline next to a field label so the user can
// tell whether a value was scraped, NMC-authoritative or AI-generated.
function SourceChip({ chip }) {
  if (!chip) return null;
  const Icon = chip.icon;
  return (
    <span
      className={`inline-flex items-center justify-center gap-0.5 ${Icon ? "w-[18px] h-[18px] p-0" : "px-1.5 py-0.5"} text-[8px] font-bold uppercase tracking-wider rounded-full align-middle ${chip.bg} ${chip.fg}`}
      title={chip.title}
      aria-label={chip.label}
    >
      {Icon ? <Icon size={11} weight="fill" /> : chip.label}
    </span>
  );
}

function InfoCell({ icon: Icon, label, value, noBorder, hint, sourceChip, verified }) {
  return (
    <div className={`p-5 ${!noBorder ? "border-r border-zinc-200" : ""}`} title={hint || undefined}>
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <Icon size={12} weight="bold" className="text-[#6043EF]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">{label}</span>
        {hint && <span className="text-[9px] text-zinc-400 cursor-help" title={hint}>ⓘ</span>}
        <SourceChip chip={sourceChip} />
      </div>
      <span className="font-['IBM_Plex_Mono'] text-lg font-bold text-[#09090B] inline-flex items-center gap-1.5">
        {value}
        {verified && (
          <SealCheck
            size={16} weight="fill" className="text-[#00A03D] shrink-0"
            title="Verified — the seat-matrix category breakdown matches the authoritative intake"
            data-testid="total-seats-verified"
          />
        )}
      </span>
    </div>
  );
}

function ProfileRow({ icon: Icon, label, value, testid, sourceChip }) {
  return (
    <div className="p-4 border-r border-b border-zinc-200" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <Icon size={12} weight="bold" className="text-[#6043EF]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">{label}</span>
        <SourceChip chip={sourceChip} />
      </div>
      <span className="font-sans text-sm font-semibold text-[#09090B]">{value}</span>
    </div>
  );
}


// ── Seat-matrix reconciliation ──────────────────────────────────────────────
// A matrix "reconciles" when its FULL-MATRIX / FIRST-ROUND category breakdown
// sums EXACTLY to the authoritative intake (NMC sanctioned seats if known, else
// the SM's own published total). Later rounds (r2/r3/stray) are never checked.
const _catSum = (cats) => Object.values(cats || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
const _isFullOrR1 = (t) => !t || t === "full_seat_matrix" || t === "provisional_r1";
// Returns "nmc" | "matrix" (reconciled, against which reference) or false.
function smRowReconciled(sm, nmcSeats) {
  if (!_isFullOrR1(sm.type)) return false;
  const s = _catSum(sm.category_seats);
  if (s <= 0) return false;
  const nmc = nmcSeats?.[sm.year] ? (sm.course === "BDS" ? nmcSeats[sm.year].bds : nmcSeats[sm.year].mbbs) : 0;
  if (nmc > 0) return s === nmc ? "nmc" : false;
  return (sm.total_seats || 0) > 0 && s === sm.total_seats ? "matrix" : false;
}
// College-level: reconciled if the best tier present (full matrix preferred,
// else R1) exists for the latest year and ALL its rows reconcile.
function seatMatrixReconciled(matrices, nmcSeats) {
  if (!matrices || !matrices.length) return false;
  const latest = Math.max(...matrices.map((m) => m.year || 0));
  const inYr = matrices.filter((m) => (m.year || 0) === latest);
  const full = inYr.filter((m) => !m.type || m.type === "full_seat_matrix");
  const r1 = inYr.filter((m) => m.type === "provisional_r1");
  const primary = full.length ? full : r1;
  return primary.length > 0 && primary.every((sm) => smRowReconciled(sm, nmcSeats));
}

// ── Seat-matrix tab helpers (module scope: shared by the year card + inspector) ──
const smTabKey = (sm) => `${sm.type || "full"}__${sm.counselling_body || ""}`;
const smTabLabel = (sm) => {
  const t = sm.type;
  if (!t) return "FULL MATRIX";
  if (t.startsWith("provisional_r")) return `PROV. R${t.replace("provisional_r", "")}`;
  // stray_special is the alias for special-stray round 1; keep order before stray_r
  if (t === "stray_special") return "Sp.Stray R1";
  if (t.startsWith("stray_special_r")) return `Sp.Stray R${t.replace("stray_special_r", "")}`;
  if (t.startsWith("stray_r")) return `Stray R${t.replace("stray_r", "")}`;
  return t.toUpperCase();
};
const smBodyTag = (sm) => {
  const b = sm.counselling_body || "";
  if (!b) return null;
  const isMcc = b === "MCC";
  return (
    <span className={`ml-1.5 text-[8px] font-bold uppercase px-1 py-0.5 rounded ${isMcc ? "bg-[#6043EF] text-white" : "bg-zinc-200 text-[#52525B]"}`}>
      {isMcc ? "MCC" : b.replace(/_/g, " ")}
    </span>
  );
};

// Admin-only per-college Seat-Matrix inspector/cleanup — PER YEAR, GROUPED BY SOURCE.
// An ⓘ next to the year header opens a dialog that lists, for the whole year, every
// source that fed the matrix — each ingestion file (parser or agent upload), an
// agent-in-chat group, or a no-file group — with that source's category cells
// underneath. ✕ on a chip removes that category (adjusts total); the trash deletes
// the whole row. Each delete snapshots a backup and re-derives seats.
function SeatMatrixInspector({ code, year }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getCollegeSeatMatrixRows(code, year);
      setData(data);
    } catch (e) {
      toast.error(`Failed to load seat-matrix rows: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };
  const onOpenChange = (v) => { setOpen(v); if (v) { setData(null); load(); } };
  const fmtTs = (s) => { try { return s ? new Date(s).toLocaleString() : ""; } catch { return s || ""; } };

  const delRow = async (row) => {
    const idf = [row.type || "FULL MATRIX", row.quota, row.course].filter(Boolean).join(" · ");
    if (!window.confirm(`Delete the ENTIRE row?\n\n${idf}\nTotal ${row.total_seats ?? "—"}\n\nA backup is snapshotted first.`)) return;
    setBusy(true);
    try {
      const { data: res } = await deleteCollegeSeatMatrixRows(code, [row.id]);
      toast.success(`Deleted ${res.deleted} row${res.deleted === 1 ? "" : "s"}${res.backup ? ` · backup ${res.backup}` : ""}. Reload to see the updated matrix.`);
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const delCategory = async (row, cat, val) => {
    const isNum = typeof val === "number";
    const newTotal = Math.max(0, (row.total_seats || 0) - (isNum ? val : 0));
    const last = Object.keys(row.categories || {}).length === 1;
    if (!window.confirm(
      `Remove category "${cat}" (${val} seat${val === 1 ? "" : "s"})?\n\n` +
      (last ? `This is the only category — the row will be deleted.\n\n`
            : `Total ${row.total_seats ?? "—"} → ${newTotal}.\n\n`) +
      `A backup is snapshotted first.`)) return;
    setBusy(true);
    try {
      const { data: res } = await deleteCollegeSeatMatrixCategories(code, row.id, [cat]);
      toast.success(res.deleted_row
        ? `Removed "${cat}" — no categories left, row deleted${res.backup ? ` · backup ${res.backup}` : ""}.`
        : `Removed "${cat}". New total ${res.new_total}${res.backup ? ` · backup ${res.backup}` : ""}.`);
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const groups = data?.groups || [];
  const isAgent = (s) => s === "agent_file" || s === "agent_inchat";

  return (
    <>
      <button
        onClick={() => onOpenChange(true)}
        title="Admin: inspect sources & clean up this year's seat matrix"
        data-testid={`sm-inspect-${year}`}
        className="flex items-center text-zinc-500 hover:text-white transition-colors"
      >
        <Info size={16} weight="bold" />
      </button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
          {/* Colorful header (matches the Ingestion Diary cleanup card) */}
          <div className="flex items-start gap-3 p-4 border-b border-rose-100 bg-rose-50">
            <Trash size={22} weight="bold" className="text-rose-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <DialogTitle className="text-base font-bold text-[#09090B]">Clean up seat-matrix rows</DialogTitle>
              <div className="text-xs text-zinc-600 mt-0.5">{year} · all sources · categories grouped by the file that introduced them</div>
            </div>
          </div>
          <div className="p-4 space-y-3 max-h-[62vh] overflow-y-auto">
            <div className="text-[10px] text-zinc-400">✕ on a chip removes that category (adjusts total); the trash icon deletes the whole row.</div>
            {loading ? (
              <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
            ) : groups.length ? groups.map((g, gi) => (
              <div key={gi} className="rounded border border-zinc-200 overflow-hidden">
                {/* Source header: parser/agent + file (or in-chat) */}
                <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#FAFAFA] border-b border-zinc-100 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${isAgent(g.source) ? "bg-violet-100 text-violet-700" : "bg-zinc-200 text-zinc-700"}`}>{g.label}</span>
                  {g.run?.file_name && <span className="truncate text-zinc-700" title={g.run.file_name}>{g.run.file_name}</span>}
                  {g.source === "agent_inchat" && <span className="text-zinc-400 italic">set in chat (no file)</span>}
                  {g.run?.link && (
                    <a href={g.run.link} target="_blank" rel="noreferrer" className="ml-auto text-[#6043EF] inline-flex items-center gap-0.5 shrink-0 font-bold">
                      <ArrowSquareOut size={12} />open
                    </a>
                  )}
                  {g.run?.ingested_at && <span className="text-zinc-400 shrink-0 ml-auto">{(g.run.ingested_at || "").slice(0, 10)}</span>}
                </div>
                {/* Rows under this source → category chips */}
                <div className="p-2 space-y-2">
                  {g.rows.map((row) => (
                    <div key={row.id}>
                      <div className="flex items-center gap-2 text-sm mb-1.5">
                        <span className="font-bold uppercase tracking-wide text-zinc-900">{row.type ? smTabLabel(row) : "FULL MATRIX"}</span>
                        {row.course && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200">
                            {row.course}
                          </span>
                        )}
                        {row.quota && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 border border-indigo-200">
                            {row.quota}
                          </span>
                        )}
                        {row.updated_at && <span className="text-[11px] text-zinc-400" title="Last written">· {fmtTs(row.updated_at)}</span>}
                        <span className="ml-auto text-xs font-semibold text-zinc-700">total {row.total_seats ?? "—"}</span>
                        <button
                          onClick={() => delRow(row)}
                          disabled={busy}
                          title="Delete the ENTIRE row"
                          data-testid={`sm-delete-${row.id}`}
                          className="text-rose-600 hover:text-rose-800 disabled:opacity-40 shrink-0"
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(row.categories || {}).length ? Object.entries(row.categories).map(([k, v]) => (
                          <span key={k} className="inline-flex items-center gap-1 text-xs pl-2 pr-1.5 py-1 rounded-md bg-zinc-100 text-zinc-800 border border-zinc-200">
                            <span><span className="font-bold">{k}</span> {v}</span>
                            <button
                              onClick={() => delCategory(row, k, v)}
                              disabled={busy}
                              title={`Delete category ${k}`}
                              data-testid={`sm-catdel-${row.id}-${k}`}
                              className="text-zinc-400 hover:text-rose-600 disabled:opacity-40"
                            >
                              <X size={10} weight="bold" />
                            </button>
                          </span>
                        )) : <span className="text-[11px] text-zinc-400 italic">no categories</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )) : <div className="py-8 text-center text-sm text-zinc-500">No seat-matrix rows for {year}.</div>}
          </div>
          <DialogFooter className="px-4 py-3 border-t border-zinc-100 bg-[#FAFAFA]">
            <button onClick={() => setOpen(false)} className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-800 mr-auto">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SeatMatrixByYear({ matrices, nmcSeats, isAdmin, code }) {
  // Group by year, newest first; each year is its own card (holds the active-tab state).
  const byYear = {};
  matrices.forEach((sm) => {
    const yr = sm.year;
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(sm);
  });
  const years = Object.keys(byYear).sort((a, b) => b - a);
  return (
    <div className="space-y-6">
      {years.map((yr) => (
        <SeatMatrixYearCard key={yr} yr={yr} items={byYear[yr]} nmcSeats={nmcSeats} isAdmin={isAdmin} code={code} />
      ))}
    </div>
  );
}

// One year's seat matrix. Tabs are CONTROLLED so the admin ⓘ inspector can scope
// to whichever tab is currently selected (its body + round + course).
function SeatMatrixYearCard({ yr, items, nmcSeats, isAdmin, code }) {
  // Sort: full first, then provisional_r1, r2, r3...
  items.sort((a, b) => {
    const order = (t) => !t ? 0 : parseInt(t.replace("provisional_r", "")) || 99;
    return order(a.type) - order(b.type);
  });
  const defaultTab = smTabKey(items[0]);

  // Verified badge: check the full-matrix (else R1) rows for this year.
  const _full = items.filter((s) => !s.type || s.type === "full_seat_matrix");
  const _r1 = items.filter((s) => s.type === "provisional_r1");
  const _primary = _full.length ? _full : _r1;
  const _recVals = _primary.map((sm) => smRowReconciled(sm, nmcSeats));
  const yearVerified = _primary.length > 0 && _recVals.every(Boolean);
  const verifiedAgainst = yearVerified && _recVals.every((v) => v === "nmc")
    ? "NMC-approved intake" : "the published seat-matrix total";

  return (
    <div className="border border-zinc-200 border-t-0" data-testid={`seat-matrix-year-${yr}`}>
      {/* Year header */}
      <div className="p-3 bg-[#09090B] flex items-center gap-3">
        <span className="font-['IBM_Plex_Mono'] text-sm font-bold text-white">{yr}</span>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">SEAT MATRIX</span>
        {isAdmin && code && <SeatMatrixInspector code={code} year={yr} />}
        {yearVerified && (
          <span
            className="flex items-center gap-1 text-[#4ADE80]"
            title={`Verified — the category breakdown sums exactly to ${verifiedAgainst}`}
            data-testid={`seat-matrix-verified-${yr}`}
          >
            <SealCheck size={14} weight="fill" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Verified</span>
          </span>
        )}
        <span className="font-['IBM_Plex_Mono'] text-xs text-zinc-500 ml-auto flex items-center gap-1">
          {items.length === 1 && smBodyTag(items[0])}
          {items.find(s => !s.type)?.total_seats || items[0]?.total_seats} total seats
        </span>
      </div>

      {items.length === 1 ? (
        <SeatMatrixGrid sm={items[0]} />
      ) : (
        <Tabs defaultValue={defaultTab} className="w-full">
          {[["MCC", items.filter(s => (s.counselling_body || "") === "MCC")],
            ["STATE", items.filter(s => (s.counselling_body || "") !== "MCC")]]
            .filter(([, rows]) => rows.length).map(([grp, rows]) => (
            <div key={grp} className="flex items-stretch border-b border-zinc-200 bg-[#F4F4F5]">
              <span className="flex items-center px-3 text-[9px] font-bold uppercase tracking-[0.12em] text-[#52525B] bg-zinc-200 shrink-0 min-w-[92px]">
                {grp === "MCC" ? "MCC" : (rows[0]?.counselling_body || "STATE").replace(/_/g, " ")}
              </span>
              <TabsList className="flex-1 flex-wrap justify-start bg-transparent rounded-none h-auto min-h-9 p-0 gap-0">
                {rows.map((sm) => {
                  const intake = nmcSeats?.[yr] ? (sm.course === "BDS" ? nmcSeats[yr].bds : nmcSeats[yr].mbbs) : 0;
                  const showPct = (!sm.type || sm.type === "provisional_r1") && intake > 0 && sm.total_seats > 0;
                  const pct = showPct ? Math.round((sm.total_seats / intake) * 100) : null;
                  return (
                  <TabsTrigger
                    key={smTabKey(sm)}
                    value={smTabKey(sm)}
                    className="rounded-none text-[10px] font-bold uppercase tracking-[0.15em] px-4 h-9 data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] data-[state=active]:text-[#6043EF]"
                    data-testid={`matrix-tab-${yr}-${smTabKey(sm)}`}
                  >
                    {smTabLabel(sm)}
                    <span className="ml-1.5 font-['IBM_Plex_Mono'] text-[10px] opacity-60">{sm.total_seats}</span>
                    {pct != null && (
                      <span
                        className="ml-1.5 font-['IBM_Plex_Mono'] text-[9px] font-bold px-1 py-0.5 rounded bg-[#00C04B]/10 text-[#00A03D]"
                        title={`${sm.total_seats} of ${intake} approved ${sm.course} seats (${sm.counselling_body || ""})`}
                      >
                        {pct}%
                      </span>
                    )}
                  </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          ))}
          {items.map((sm) => (
            <TabsContent key={smTabKey(sm)} value={smTabKey(sm)} className="mt-0">
              <SeatMatrixGrid sm={sm} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function SeatMatrixGrid({ sm }) {
  const entries = Object.entries(sm.category_seats || {}).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-[#52525B]">No category data available</div>
    );
  }
  return (
    <div className="grid grid-cols-4 sm:grid-cols-7 gap-0">
      {entries.map(([cat, seats]) => (
        <div key={cat} className="p-4 border-r border-b border-zinc-200 text-center">
          <Badge className={`${CATEGORY_COLORS[cat] || "bg-zinc-100 text-zinc-600 border border-zinc-300"} text-[10px] uppercase tracking-wider mb-2`}>
            {cat}
          </Badge>
          <div className="font-['IBM_Plex_Mono'] text-2xl font-bold text-[#09090B]">{seats}</div>
        </div>
      ))}
    </div>
  );
}

const QUOTA_LABELS = { MG: "GOVT", MP: "PRIVATE", MN: "NRI", MQ: "MGMT/OTHER" };

function FeeQuotaGrid({ quotas }) {
  const entries = Object.entries(quotas);
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-${Math.min(entries.length, 4)} gap-0`}>
      {entries.map(([quota, info]) => (
        <div key={quota} className="p-4 border-r border-b border-zinc-200" data-testid={`fee-quota-${quota}`}>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] block mb-1">
            {QUOTA_LABELS[quota] || quota}
          </span>
          <span className="font-['IBM_Plex_Mono'] text-lg font-bold text-[#09090B]">
            {formatFees(info.min)}
          </span>
          {info.min !== info.max && (
            <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]"> — {formatFees(info.max)}</span>
          )}
          <span className="block font-['IBM_Plex_Mono'] text-[10px] text-zinc-400 mt-0.5">{info.count} allotments</span>
        </div>
      ))}
    </div>
  );
}


function CutoffMultiFilter({ selected, onToggle, onClear, options, testid }) {
  const label = selected.length === 0 ? "All" : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex h-7 w-full items-center justify-between rounded-md border border-zinc-300 bg-transparent px-3 text-[11px] font-sans font-normal normal-case tracking-normal text-left"
          data-testid={testid}
        >
          <span className={selected.length ? "text-[#09090B]" : "text-zinc-500"}>{label}</span>
          <CaretRight size={11} className="rotate-90 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2 max-h-72 overflow-y-auto" align="start">
        <div className="flex items-center justify-between px-1 pb-1 mb-1 border-b border-zinc-100">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Category</span>
          {selected.length > 0 && (
            <button onClick={onClear} className="text-[10px] font-bold uppercase text-[#6043EF] hover:underline" data-testid={`${testid}-clear`}>Clear</button>
          )}
        </div>
        {options.map((cat) => (
          <label key={cat} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-zinc-50 cursor-pointer" data-testid={`${testid}-opt-${cat}`}>
            <Checkbox checked={selected.includes(cat)} onCheckedChange={() => onToggle(cat)} />
            <span className="text-xs text-[#09090B]">{cat}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function CutoffFilter({ value, onChange, options, placeholder = "All", testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-[11px] border-zinc-300 font-sans font-normal normal-case tracking-normal" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
